import React, { useState, useEffect, useRef } from 'react';
import { getMessages, sendMessage, sendImage, getGroupMessages, sendGroupMessage, sendGroupImage, sendVideo, sendGroupVideo, reactToMessage, deleteMessage, editMessage, getConversations, getUserGroups, getAllUsers, sendAudio, sendGroupAudio, forwardAudio, sendDocument, sendGroupDocument, markAsRead, markGroupAsRead, blockUser, unblockUser, muteConversation, unmuteConversation, addFavorite, removeFavorite, getUserStatus, clearChat, deleteConversation, sendEditedDocument, forwardVideo, acknowledgeGroup, leaveGroup, deleteGroup, findConversationWith } from '../../services/api';
import { joinConversation, joinGroup, onNewMessage, onNewGroupMessage, onMessageConfirmed, onMessageEdited, onMessageTranscriptionReady, emitTyping, emitStopTyping, getSocket } from '../../services/socket';
import GroupSettingsModal from '../GroupSettingsModal/GroupSettingsModal';
import DocumentModal from '../DocumentModal/DocumentModal';
import ForwardModal from './ForwardModal';
import ContactPickerModal from '../ContactPickerModal/ContactPickerModal';
import ChannelPickerModal from '../ChannelPickerModal/ChannelPickerModal';
import SharedContactProfile from '../SharedContactProfile/SharedContactProfile';
import { correctText } from '../../services/textCorrection.service';
import { v2Subscribe } from '../../services/api';
import { Icons } from '../Icons/Icons';
import TtsButtons from './TtsButtons';
import SupportChat from '../Support/SupportChat';
import t from '../../i18n/useTranslation';
import { langToISO } from '../../utils/langCode';
import ui from '../../i18n/ui';
import CameraModal from '../CameraModal/CameraModal';
import PublishModal from '../ChannelsV2/PublishModal';
import { v2GetUserChannels } from '../../services/api';
import './ChatWindow.css';
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { linkify } from '../../utils/linkify';
import { downloadMedia } from '../../utils/downloadMedia';
import useModalClose from '../../hooks/useModalClose';
import { formatDayHeader } from '../../utils/formatDate';
import ImageViewer from '../ImageViewer/ImageViewer';
import UserInfoModal from '../UserInfoModal/UserInfoModal';

function ChatWindow({ conversation, currentUser, onBack, onlineUsers }) {
    // Hardware back de Android (botón físico o swipe-from-edge): cierra
    // el chat y vuelve a la lista, en vez de salir de la app. Solo activo
    // cuando hay una conversación abierta. Si dentro del chat hay un
    // modal/viewer abierto, el hook ya gestiona el LIFO y lo cierra
    // primero antes de afectar al chat.
    useModalClose(onBack, !!conversation);

    const [messages, setMessages] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    // Nota: el patrón optimistic (limpiar input al instante y mostrar
    // burbuja como pendiente) hace innecesario un estado `sendingText`.
    // Si el usuario tap-tap rápido el segundo tap encuentra newMessage
    // vacío y `if (!text) return` lo bloquea. Sin doble envío posible.
    const [loading, setLoading] = useState(true);
    const messagesEndRef = useRef(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [videoPreviewPoster, setVideoPreviewPoster] = useState(null);
    const fileInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const audioInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const [isRecording, setIsRecording] = useState(false);
    const [audioPreview, setAudioPreview] = useState(null);
    const [audioBlob, setAudioBlob] = useState(null);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const recordingTimerRef = useRef(null);
    const documentInputRef = useRef(null);
    const galleryInputRef = useRef(null);
    const [documentFile, setDocumentFile] = useState(null);
    const [documentPreview, setDocumentPreview] = useState(null);
    const [showGroupSettings, setShowGroupSettings] = useState(false);
    const [groupSettingsTab, setGroupSettingsTab] = useState('members');
    const [documentModal, setDocumentModal] = useState(null);
    const [devToast, setDevToast] = useState(null); // solo para desarrollo
    const [reactionBar, setReactionBar] = useState(null);
    const [actionBar, setActionBar] = useState(null);
    const [replyingTo, setReplyingTo] = useState(null);
    const [swipingMsgId, setSwipingMsgId] = useState(null);
    const [swipeOffset, setSwipeOffset] = useState(0);
    const swipeStartX = useRef(0);
    const [editingMessage, setEditingMessage] = useState(null);
    const [reactionsModal, setReactionsModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);
    // Modo selección múltiple estilo WhatsApp.
    // selectionMode: bool — cuando true, los taps cortos en mensajes hacen toggle en lugar de abrir el actionBar.
    // selectedMessageIds: Set<string> — ids de mensajes actualmente seleccionados.
    // bulkDeleteModal: bool | null — controla el modal de confirmación para borrado masivo.
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());
    const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
    const [forwardModal, setForwardModal] = useState(null);
    const [forwardTargets, setForwardTargets] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const longPressTimer = useRef(null);
    const longPressActive = useRef(false);
    const [isTyping, setIsTyping] = useState(false);
    const [typingUser, setTypingUser] = useState(null);
    const typingTimerRef = useRef(null);
    const ttsCheckIntervalRef = useRef(null);
    const [showMenu, setShowMenu] = useState(false);
    const [showLeaveGroupModal, setShowLeaveGroupModal] = useState(false);
    const [isBlocked, setIsBlocked] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);
    // Banner para remitentes que NO están en tu agenda + form para añadirlos.
    const [strangerDismissed, setStrangerDismissed] = useState(false);
    const [showAddContactForm, setShowAddContactForm] = useState(false);
    // Para el banner de "has sido añadido a este grupo": local optimistic.
    // El estado real está en conversation.acknowledged (viene del backend).
    const [groupAck, setGroupAck] = useState(false);
    const isGroup = conversation?.type === 'group';
    const showGroupWelcome = isGroup
        && conversation?.id
        && conversation?.acknowledged === false
        && !groupAck;

    const handleGroupAccept = async () => {
        if (!conversation?.id) return;
        setGroupAck(true);
        try {
            await acknowledgeGroup(conversation.id);
        } catch (e) {
            console.warn('acknowledgeGroup failed:', e?.message);
        }
    };

    const handleGroupLeave = async () => {
        if (!conversation?.id) return;
        // leaveGroupConfirm existe en t.chat.* para los 14 idiomas
        if (!window.confirm(t.chat?.leaveGroupConfirm || '¿Salir de este grupo?')) return;
        try {
            await leaveGroup({ groupId: conversation.id, userId: currentUser._id });
            setGroupAck(true);
            try { window.dispatchEvent(new CustomEvent('kaixo:left_group', { detail: { groupId: conversation.id } })); } catch {}
            if (onBack) onBack();
        } catch (e) {
            alert((t.chat?.errorLeaveGroup || 'No se pudo salir del grupo') + ': ' + (e?.response?.data?.error || e.message));
        }
    };

    // Eliminar grupo (solo admin / creador). Borra el grupo para todos los
    // miembros. Operación destructiva — confirma dos veces.
    const handleDeleteGroup = async () => {
        if (!conversation?.id) return;
        if (!window.confirm(t.chat?.deleteGroupConfirm || '¿Eliminar este grupo para todos los miembros? Esta acción es irreversible.')) return;
        try {
            await deleteGroup({ groupId: conversation.id, userId: currentUser._id });
            try { window.dispatchEvent(new CustomEvent('conversations_updated')); } catch {}
            if (onBack) onBack();
        } catch (e) {
            alert((t.chat?.errorDeleteGroup || 'No se pudo eliminar el grupo') + ': ' + (e?.response?.data?.error || e.message));
        }
    };

    const handleStrangerBlock = async () => {
        if (!conversation?.otherUser?.id) return;
        try {
            await blockUser({ userId: currentUser._id, targetId: conversation.otherUser.id });
            setIsBlocked(true);
            setStrangerDismissed(true);
        } catch (e) {
            console.warn('block failed:', e?.message);
        }
    };
    const [searchMode, setSearchMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchIndex, setSearchIndex] = useState(0);
    const [activeTtsId, setActiveTtsId] = useState(null);
    const [ttsPausedId, setTtsPausedId] = useState(null);
    const [showAttachMenu, setShowAttachMenu] = useState(false);
    const [showContactPicker, setShowContactPicker] = useState(false);
    const [showChannelPicker, setShowChannelPicker] = useState(false);
    // IDs de canales a los que el usuario YA se ha suscrito en esta sesión
    // tras pulsar "Seguir" en una invitación. Permite cambiar el botón a
    // "Siguiendo ✓" sin necesidad de refrescar la lista de canales.
    const [followedChannelsLocal, setFollowedChannelsLocal] = useState(new Set());
    const [showAttachSubMenu, setShowAttachSubMenu] = useState(false);
    const menuRef = useRef(null);
    const attachMenuRef = useRef(null);
    const [showCamera, setShowCamera] = useState(false);
    // Selector de destino tras foto: si el user tiene canal de Latidos
    // (cargado lazy al abrir cámara), permite publicar en Latidos o
    // crear historia además de enviar al chat. Si no tiene canal, esos
    // botones aparecen deshabilitados con tooltip "Crea tu canal primero".
    const [myFirstChannel, setMyFirstChannel] = useState(null);
    // pendingPublish: { file, type, mode: 'latido' | 'story' } cuando
    // el user elige publicar en Latidos desde la cámara. Abre PublishModal.
    const [pendingPublish, setPendingPublish] = useState(null);

    // Cargar canal de Latidos del user — lazy, solo la primera vez que
    // abre la cámara para no añadir una request al render inicial. Si el
    // user tiene varios canales, usamos el primero (en futuro: selector).
    useEffect(() => {
        if (!showCamera || myFirstChannel || !currentUser?._id) return;
        let cancelled = false;
        v2GetUserChannels(currentUser._id)
            .then(({ data }) => {
                if (cancelled) return;
                const list = data?.channels || data?.data || [];
                if (Array.isArray(list) && list.length > 0) {
                    setMyFirstChannel(list[0]);
                }
            })
            .catch(() => { /* silencioso: latidosDisabled queda true */ });
        return () => { cancelled = true; };
    }, [showCamera, myFirstChannel, currentUser?._id]);

    const [imageViewer, setImageViewer] = useState(null);
    const [showUserInfo, setShowUserInfo] = useState(false);
    // En grupos: click en el nombre/avatar de un participante abre su
    // perfil. Guardamos el user object completo aquí (preferentemente de
    // allUsers; si no, fallback al sender embebido en el mensaje).
    const [groupUserInfo, setGroupUserInfo] = useState(null);
    // Ficha de contacto compartido al pulsar una burbuja contact-card.
    // Estilo nativo (avatar grande + bloques tipo Apple Contacts), distinto
    // de groupUserInfo que abre el UserInfoModal del miembro de un grupo.
    const [sharedContactInfo, setSharedContactInfo] = useState(null);
    const [showSupportPanel, setShowSupportPanel] = useState(false);
    // Detecta si el chat es con KAIXO Support (cuenta oficial) para ofrecer
    // el panel de FAQ con un botón extra en la cabecera.
    const isSupportChat = conversation?.type !== 'group' &&
        (conversation?.otherUser?.name === 'KAIXO Support' ||
         conversation?.otherUser?.isOfficial === true);
    const [showScrollDown, setShowScrollDown] = useState(false);
    // Contador estilo WhatsApp: mensajes recibidos mientras el usuario está
    // leyendo arriba. Se resetea al volver al final del scroll. Pintamos un
    // badge sobre el FAB para que sepa cuántos mensajes nuevos hay debajo.
    const [newMessagesCount, setNewMessagesCount] = useState(0);
    // Ref espejo del estado "estoy al final": lo usan los handlers de socket
    // que viven en closures viejas y no pueden leer showScrollDown sin stale.
    const isAtBottomRef = useRef(true);
    const messagesContainerRef = useRef(null);
    const [availableVoices, setAvailableVoices] = useState([]);

    const showToast = (msg, type = 'error') => {
        setDevToast({ msg, type });
        setTimeout(() => setDevToast(null), 5000);
    };

    // Extrae un mensaje amigable de un error de axios o JS.
    // Prioriza: mensaje del servidor → stack propio en lenguaje natural → fallback genérico.
    const friendlyError = (err, fallback = 'Algo salió mal') => {
        if (!err) return fallback;
        const server = err.response?.data?.error || err.response?.data?.message;
        if (server && typeof server === 'string' && server.length < 200) return server;
        const m = err.message || '';
        if (/network/i.test(m)) return 'Sin conexión con el servidor';
        if (/timeout/i.test(m)) return 'La conexión tardó demasiado, reintenta';
        if (/401/.test(m)) return 'Sesión expirada, inicia sesión de nuevo';
        if (/403/.test(m)) return 'No tienes permiso para esta acción';
        if (/404/.test(m)) return 'No encontrado';
        if (/413/.test(m)) return 'Archivo demasiado grande';
        if (/429/.test(m)) return 'Demasiadas peticiones, espera un momento';
        // Si llega con mensaje técnico largo o stack, devolver fallback
        if (!m || m.length > 120) return fallback;
        return m;
    };



    useEffect(() => {
    if (!window.speechSynthesis) return;
    const loadVoices = () => setAvailableVoices(window.speechSynthesis.getVoices());
    loadVoices();
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
}, []);

    // Cargar usuarios una sola vez (para mostrar nombres en reacciones, etc.)
    useEffect(() => {
        if (allUsers.length === 0) {
            getAllUsers().then(res => {
                setAllUsers((res.data.users || []).filter(u => u._id !== currentUser._id && !u.phone?.startsWith('widget_')));
            }).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!conversation) return;
        loadMessages();
        if (conversation.type === 'group') { joinGroup(conversation.id); }
        else { joinConversation(conversation.id); }
        const socket = getSocket();
        // Helper: si el mensaje del socket es del propio user y hay
        // una burbuja optimistic pendiente con mismo texto, la
        // promovemos (le ponemos el id real). Esto evita el bug de
        // duplicado clásico: socket llega antes que la response del
        // POST, el optimistic todavía tenía tempId, el id real del
        // socket no matchea por id → se añadía un mensaje extra.
        // (revisión 29-abr)
        const promoteOptimisticOrAdd = (prev, incoming) => {
            // Ya existe por id real → ignorar (dedup estándar)
            if (prev.some(m => m.id?.toString() === incoming.id?.toString())) return prev;
            const senderId = incoming.sender?.id?.toString();
            const myId = currentUser?._id?.toString();
            if (senderId && senderId === myId) {
                // Buscar optimistic del propio user con mismo texto pendiente
                const idx = prev.findIndex(m =>
                    m.tempId
                    && m.isPending !== false
                    && m.sender?.id?.toString() === myId
                    && (m.text || '') === (incoming.text || '')
                );
                if (idx >= 0) {
                    const updated = prev.slice();
                    updated[idx] = { ...updated[idx], ...incoming, id: incoming.id, isPending: false };
                    return updated;
                }
            }
            return [...prev, incoming];
        };

        const handleNewMsg = (data) => {
            const convId = data.conversationId?.toString();
            const currentConvId = conversation.id?.toString();
            if (convId === currentConvId || (!currentConvId && data.message?.sender?.id?.toString() === currentUser._id?.toString())) {
                const fromMe = data.message?.sender?.id?.toString() === currentUser._id?.toString();
                setMessages(prev => promoteOptimisticOrAdd(prev, data.message));
                // Si el usuario está leyendo arriba y llega un mensaje ajeno,
                // sumamos al contador (WhatsApp). Mensajes propios no cuentan.
                if (!fromMe && !isAtBottomRef.current) {
                    setNewMessagesCount(c => c + 1);
                }
                if (conversation.type !== 'group') {
                    markAsRead(conversation.id, { userId: currentUser._id }).then(() => window.dispatchEvent(new Event('conversations_updated'))).catch(() => {});
                }
            } else { window.dispatchEvent(new Event('conversations_updated')); }
        };
        const handleNewGroupMsg = (data) => {
            if (data.groupId?.toString() === conversation.id?.toString()) {
                const fromMe = data.message?.sender?.id?.toString() === currentUser._id?.toString();
                setMessages(prev => promoteOptimisticOrAdd(prev, data.message));
                if (!fromMe && !data.message?.isSystemMessage && !isAtBottomRef.current) {
                    setNewMessagesCount(c => c + 1);
                }
                // Estoy DENTRO del grupo viendo el mensaje → marcarlo como
                // leído al instante para que el badge no aparezca en la lista.
                // (El backend incrementaba unreadCount del receptor sí o sí;
                // sin este reset, al volver atrás veríamos un badge "fantasma".)
                if (!data.message?.isSystemMessage) {
                    markGroupAsRead({ groupId: conversation.id, userId: currentUser._id })
                        .then(() => window.dispatchEvent(new Event('conversations_updated')))
                        .catch(() => {});
                    return; // dispatch ya hecho dentro del then
                }
            }
            window.dispatchEvent(new Event('conversations_updated'));
        };
        const handleMessagesRead = (data) => {
            if (data.conversationId?.toString() === conversation.id?.toString()) {
                setMessages(prev => prev.map(msg => msg.sender?.id?.toString() === currentUser._id?.toString() ? { ...msg, status: 'read' } : msg));
            }
        };
        const handleReaction = (data) => {
            setMessages(prev => prev.map(msg => msg.id?.toString() === data.messageId?.toString() ? { ...msg, reactions: data.reactions } : msg));
        };
        const handleDeleted = (data) => {
            if (data.deleteForAll) {
                setMessages(prev => prev.map(msg => msg.id?.toString() === data.messageId ? { ...msg, text: t.chat.deleted, mediaType: null, mediaUrl: null, isDeleted: true, reactions: [] } : msg));
            } else {
                if (data.userId === currentUser._id) { setMessages(prev => prev.filter(msg => msg.id?.toString() !== data.messageId)); }
            }
        };
        const handleTyping = (data) => { if (data.userId !== currentUser._id) { setTypingUser(data.userName); setIsTyping(true); } };
        const handleStopTyping = (data) => { if (data.userId !== currentUser._id) { setIsTyping(false); setTypingUser(null); } };
        // Cuando el backend confirma el mensaje con traducción, actualizar el globo del emisor
        const handleConfirmed = (data) => {
            if (data.conversationId?.toString() !== conversation?.id?.toString()) return;
            setMessages(prev => prev.map(m =>
                (m.id?.toString().startsWith('temp_') && m.timestamp === data.tempTimestamp)
                    ? { ...m, ...data.message, id: data.message.id }
                    : m
            ));
        };

        // Cuando llega un mensaje editado, actualizar en la lista local
        const handleEdited = (data) => {
            setMessages(prev => prev.map(m => {
                if (m.id?.toString() !== data.messageId?.toString()) return m;
                return {
                    ...m,
                    text: data.text,
                    originalText: data.originalText || m.originalText,
                    translatedText: data.translatedText !== undefined ? data.translatedText : m.translatedText,
                    isEdited: true,
                    editedAt: data.editedAt
                };
            }));
        };

        // Cuando el backend termina de traducir un mensaje del sender,
        // emite 'message_translated' para que la UI del sender pueda
        // mostrar el doble check (delivered) y opcionalmente la traducción
        // ya disponible para el receptor.
        const handleTranslated = (data) => {
            if (!data?.messageId) return;
            setMessages(prev => prev.map(m =>
                String(m.id) === String(data.messageId)
                    ? {
                        ...m,
                        status: m.status === 'sending' || !m.status ? 'delivered' : m.status,
                        translatedText: data.translatedText || m.translatedText,
                        receiverLanguage: data.receiverLanguage || m.receiverLanguage,
                        isPending: false
                    }
                    : m
            ));
        };

        // Self-healing: cuando getMessages detecta un mensaje sin traducción
        // que el receptor necesita, traduce en background y emite este evento
        // con el texto traducido. Actualizamos la burbuja para que el receptor
        // vea la traducción aparecer sin tener que refrescar.
        const handleTranslationUpdated = (data) => {
            if (!data?.messageId || !data?.text) return;
            setMessages(prev => prev.map(m =>
                String(m.id) === String(data.messageId)
                    ? {
                        ...m,
                        text: data.text,
                        language: data.language || m.language,
                        isOriginal: false
                    }
                    : m
            ));
        };

        // FASE 2 del pipeline de video: subtítulos/transcripción listos.
        // Reemplaza el indicador "Transcribiendo..." por los datos reales.
        const handleTranscriptionReady = (data) => {
            if (!data?.messageId) return;
            setMessages(prev => prev.map(m =>
                String(m.id) === String(data.messageId)
                    ? {
                        ...m,
                        transcription: data.transcription !== undefined ? data.transcription : m.transcription,
                        translatedTranscription: data.translatedTranscription !== undefined ? data.translatedTranscription : m.translatedTranscription,
                        originalTranscription: data.originalTranscription !== undefined ? data.originalTranscription : m.originalTranscription,
                        subtitlesUrl: data.subtitlesUrl || m.subtitlesUrl || '',
                        transcriptionPending: false
                    }
                    : m
            ));
        };

        const cleanupMsg = onNewMessage(handleNewMsg);
        const cleanupGroupMsg = onNewGroupMessage(handleNewGroupMsg);
        const cleanupConfirmed = onMessageConfirmed(handleConfirmed);
        const cleanupEdited = onMessageEdited(handleEdited);
        const cleanupTranscription = onMessageTranscriptionReady(handleTranscriptionReady);
        if (socket) {
            // Defensa: si algún listener previo sobrevivió (por remount sin
            // cleanup o doble efecto en dev), lo quitamos primero.
            socket.off('message_reaction', handleReaction);
            socket.off('message_deleted', handleDeleted);
            socket.off('messages_read', handleMessagesRead);
            socket.off('user_typing', handleTyping);
            socket.off('user_stop_typing', handleStopTyping);
            socket.off('message_translated', handleTranslated);
            socket.off('message_translation_updated', handleTranslationUpdated);
            socket.on('message_reaction', handleReaction);
            socket.on('message_deleted', handleDeleted);
            socket.on('messages_read', handleMessagesRead);
            socket.on('user_typing', handleTyping);
            socket.on('user_stop_typing', handleStopTyping);
            socket.on('message_translated', handleTranslated);
            socket.on('message_translation_updated', handleTranslationUpdated);
        }

        document.addEventListener('resume', loadMessages);

        return () => {
            cleanupMsg(); cleanupGroupMsg(); cleanupConfirmed(); cleanupEdited(); cleanupTranscription();
            if (socket) {
                socket.off('message_reaction', handleReaction);
                socket.off('message_deleted', handleDeleted);
                socket.off('messages_read', handleMessagesRead);
                socket.off('user_typing', handleTyping);
                socket.off('user_stop_typing', handleStopTyping);
                socket.off('message_translated', handleTranslated);
                socket.off('message_translation_updated', handleTranslationUpdated);
            }
            document.removeEventListener('resume', loadMessages);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversation]);

    // Auto-scroll SOLO si el usuario ya estaba al final. Si está leyendo
    // arriba, no le tiramos del scroll por cada mensaje nuevo — el badge
    // del FAB le avisa cuántos hay debajo.
    useEffect(() => {
        if (isAtBottomRef.current) scrollToBottom();
    }, [messages]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            // En modo selección no cerramos automáticamente — solo el botón ✕ o ESC sale.
            if (selectionMode) return;
            if (reactionBar) setReactionBar(null);
            if (actionBar) setActionBar(null);
            if (showMenu && menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
            if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) setShowAttachMenu(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [reactionBar, actionBar, showMenu, selectionMode]);

    // ESC sale del modo selección múltiple (UX desktop estándar).
    useEffect(() => {
        if (!selectionMode) return;
        const onKey = (e) => { if (e.key === 'Escape') exitSelection(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectionMode]);

    // Al desmontar el componente: cortar TTS en curso y limpiar el poller.
    useEffect(() => () => {
        if (ttsCheckIntervalRef.current) {
            clearInterval(ttsCheckIntervalRef.current);
            ttsCheckIntervalRef.current = null;
        }
        try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch {}
    }, []);

    const loadMessages = async () => {
        setLoading(true);
        try {
            // Si abrimos un chat 1-a-1 sin conversation.id (ej. desde la
            // agenda o tras reinstalar la app en otro dispositivo), antes
            // mostrábamos pantalla en blanco hasta enviar el primer mensaje.
            // Ahora intentamos resolver si la conversación ya existe entre
            // los dos usuarios; si existe, fijamos el id y seguimos cargando.
            if (!conversation.id && conversation.type !== 'group' && conversation.otherUser?.id) {
                try {
                    const r = await findConversationWith(conversation.otherUser.id, currentUser._id);
                    if (r?.data?.exists && r.data.conversation?.id) {
                        conversation.id = r.data.conversation.id;
                        joinConversation(conversation.id);
                    }
                } catch { /* no bloqueamos si falla — seguirá flujo normal */ }
            }
            if (!conversation.id) { setMessages([]); setLoading(false); return; }
            let response;
            if (conversation.type === 'group') {
                response = await getGroupMessages(conversation.id, currentUser._id);
                // Marcar como leído al abrir + dispatch para refrescar la
                // lista (antes solo se hacía para chats 1:1; el badge del
                // grupo se quedaba visible hasta el siguiente fetch).
                markGroupAsRead({ groupId: conversation.id, userId: currentUser._id })
                    .then(() => window.dispatchEvent(new Event('conversations_updated')))
                    .catch(() => {});
                getUserStatus(currentUser._id, { groupId: conversation.id }).then(res => { setIsMuted(res.data.isMuted); setIsFavorite(res.data.isFavorite); }).catch(() => {});
            } else {
                response = await getMessages(conversation.id, currentUser._id);
                getUserStatus(currentUser._id, { targetId: conversation.otherUser.id, conversationId: conversation.id }).then(res => { setIsBlocked(res.data.isBlocked); setIsMuted(res.data.isMuted); setIsFavorite(res.data.isFavorite); }).catch(() => {});
                markAsRead(conversation.id, { userId: currentUser._id }).then(() => window.dispatchEvent(new Event('conversations_updated'))).catch(() => {});
            }
            setMessages(response.data.messages);
            setHasMore(response.data.hasMore || false);
        } catch (error) { showToast(`Error al cargar mensajes: ${friendlyError(error)}`); }
        setLoading(false);
    };

    const loadMoreMessages = async () => {
        if (loadingMore || !hasMore || messages.length === 0) return;
        setLoadingMore(true);
        try {
            const oldest = messages[0]?.timestamp;
            let response;
            if (conversation.type === 'group') {
                response = await getGroupMessages(conversation.id, currentUser._id, oldest);
            } else {
                response = await getMessages(conversation.id, currentUser._id, oldest);
            }
            setMessages(prev => [...(response.data.messages || []), ...prev]);
            setHasMore(response.data.hasMore || false);
        } catch (error) { showToast(`Error al cargar más mensajes: ${friendlyError(error)}`); }
        setLoadingMore(false);
    };

    useEffect(() => {
        const videos = document.querySelectorAll('.message-video');
        videos.forEach(video => {
            const tracks = video.textTracks;
            if (tracks && tracks.length > 0) { for (let i = 0; i < tracks.length; i++) { tracks[i].mode = 'showing'; } }
        });
    }, [messages]);

    const EMOJIS = ['❤️', '👍', '😂', '😮', '😢', '😡'];

    const handleReaction = async (messageId, emoji) => {
        setReactionBar(null);
        try { await reactToMessage(messageId, { userId: currentUser._id, emoji }); }
        catch (error) { showToast(`Error al reaccionar: ${friendlyError(error)}`); }
    };

    const getReactionSummary = (reactions) => {
        if (!reactions || reactions.length === 0) return [];
        const counts = {};
        reactions.forEach(r => { counts[r.emoji] = (counts[r.emoji] || 0) + 1; });
        return Object.entries(counts).map(([emoji, count]) => ({ emoji, count }));
    };

    // ─── Color por participante en grupos ───────────────────────────────
    // Hash determinista del userId → 1 color de una paleta de 8 distinguibles.
    // El mismo user siempre obtiene el mismo color en todos los chats.
    const SENDER_COLORS = [
        '#E5732C', // naranja kaixo
        '#1FA1B0', // cyan
        '#9B5BD8', // violeta suave
        '#3B7BD8', // azul
        '#D85B8E', // rosa
        '#5BA15B', // verde
        '#C09B2D', // mostaza
        '#7E5BD8'  // morado
    ];
    const colorForSender = (userId) => {
        if (!userId) return SENDER_COLORS[0];
        const s = String(userId);
        let h = 0;
        for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
        return SENDER_COLORS[h % SENDER_COLORS.length];
    };

    // Click en el nombre o el avatar del autor de un globo (grupos).
    // Abre el modal de perfil del autor. Si tenemos al user en allUsers
    // usamos esa versión completa; si no, fallback a lo embebido en el
    // mensaje (id, name, avatar) — el modal renderiza lo que tenga.
    const openSenderProfile = (sender) => {
        if (!sender?.id) return;
        if (sender.id?.toString() === currentUser._id?.toString()) return;
        const full = allUsers.find(u => u._id?.toString() === sender.id?.toString());
        setGroupUserInfo(full || sender);
    };

    // ─── Modo selección múltiple ──────────────────────────────────────────
    // Helpers reusables. toggleSelection/exitSelection trabajan con strings
    // para mantener consistencia con el resto del código (msg.id?.toString()).
    // (enterSelection eliminado: ahora long-press abre actionBar directamente,
    // estilo WhatsApp. El modo selección sigue alcanzable desde otros sitios.)
    const toggleSelection = (msgId) => {
        const id = msgId?.toString();
        if (!id) return;
        setSelectedMessageIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            // Si se queda vacío, salir del modo selección.
            if (next.size === 0) setSelectionMode(false);
            return next;
        });
    };
    const exitSelection = () => {
        setSelectionMode(false);
        setSelectedMessageIds(new Set());
        setBulkDeleteModal(false);
    };

    // ¿Pueden borrarse "para todos"? Solo si TODOS son del usuario actual y
    // tienen menos de 1h de antigüedad (misma regla que el actionBar single).
    const canBulkDeleteForAll = () => {
        if (selectedMessageIds.size === 0) return false;
        const now = Date.now();
        const ONE_HOUR = 60 * 60 * 1000;
        for (const id of selectedMessageIds) {
            const msg = messages.find(m => m.id?.toString() === id);
            if (!msg) return false;
            if (msg.sender?.id !== currentUser._id) return false;
            if (now - new Date(msg.timestamp).getTime() >= ONE_HOUR) return false;
        }
        return true;
    };

    const handleBulkDelete = async (forAll) => {
        const ids = Array.from(selectedMessageIds);
        if (ids.length === 0) { exitSelection(); return; }
        try {
            await Promise.all(ids.map(id =>
                deleteMessage(id, { userId: currentUser._id, deleteForAll: forAll })
            ));
            const idSet = new Set(ids);
            if (forAll) {
                setMessages(prev => prev.map(msg =>
                    idSet.has(msg.id?.toString())
                        ? { ...msg, text: t.chat.deleted, mediaType: null, mediaUrl: null, isDeleted: true, reactions: [] }
                        : msg
                ));
            } else {
                setMessages(prev => prev.filter(msg => !idSet.has(msg.id?.toString())));
            }
        } catch (error) { showToast(`Error al eliminar mensajes: ${friendlyError(error)}`); }
        exitSelection();
    };

    const handleLongPressStart = (msgId) => {
        longPressActive.current = false;
        longPressTimer.current = setTimeout(() => {
            longPressActive.current = true;
            // Si ya estamos en modo selección, long-press hace toggle.
            // Si no, abrir actionBar (estilo WhatsApp): emojis + responder
            // + editar + eliminar. Antes entraba en modo selección que solo
            // mostraba la papelera arriba; el usuario nunca veía emojis.
            if (selectionMode) toggleSelection(msgId);
            else setActionBar(msgId);
        }, 600);
    };
    const handleLongPressEnd = () => { clearTimeout(longPressTimer.current); };

    const handleSwipeStart = (e, msg) => {
        if (msg.isDeleted) return;
        swipeStartX.current = e.touches?.[0]?.clientX || 0;
        setSwipingMsgId(msg.id?.toString());
        setSwipeOffset(0);
    };
    const handleSwipeMove = (e, msg) => {
        if (swipingMsgId !== msg.id?.toString()) return;
        const x = e.touches?.[0]?.clientX || 0;
        const delta = x - swipeStartX.current;
        const isSent = msg.sender.id === currentUser._id;
        const effective = isSent ? Math.min(0, delta) : Math.max(0, delta);
        setSwipeOffset(Math.min(Math.abs(effective), 80));
    };
    const buildReplyTarget = (msg) => ({
        id: msg.id,
        text: msg.text || (msg.mediaType === 'image' ? '📷 Imagen' : msg.mediaType === 'video' ? '🎥 Video' : msg.mediaType === 'audio' ? '🎤 Audio' : msg.mediaType === 'document' ? '📄 Documento' : ''),
        senderName: msg.sender?.name,
        mediaType: msg.mediaType || 'text',
        mediaUrl: msg.mediaUrl || null
    });
    const handleSwipeEnd = (msg) => {
        if (swipingMsgId !== msg.id?.toString()) return;
        if (swipeOffset >= 50) setReplyingTo(buildReplyTarget(msg));
        setSwipingMsgId(null);
        setSwipeOffset(0);
    };

    const jumpToMessage = (msgId) => {
        if (!msgId) return;
        const idx = messages.findIndex(m => String(m.id) === String(msgId) || String(m._id) === String(msgId));
        if (idx < 0) return;
        const el = document.getElementById(`msg-${idx}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('message-highlighted-flash');
            setTimeout(() => el.classList.remove('message-highlighted-flash'), 1500);
        }
    };

    const handleDeleteMessage = async (forAll) => {
        if (!deleteModal) return;
        try {
            await deleteMessage(deleteModal, { userId: currentUser._id, deleteForAll: forAll });
            if (forAll) { setMessages(prev => prev.map(msg => msg.id?.toString() === deleteModal ? { ...msg, text: t.chat.deleted, mediaType: null, mediaUrl: null, isDeleted: true, reactions: [] } : msg)); }
            else { setMessages(prev => prev.filter(msg => msg.id?.toString() !== deleteModal)); }
        } catch (error) { showToast(`Error al eliminar mensaje: ${friendlyError(error)}`); }
        setDeleteModal(null); setActionBar(null);
    };

    const handleOpenForward = async (msg) => {
        setForwardModal(msg); setForwardTargets([]);
        try {
            const [convRes, groupRes, usersRes] = await Promise.all([
                getConversations(currentUser._id),
                getUserGroups(currentUser._id),
                getAllUsers()
            ]);
            setConversations(convRes.data.conversations || []);
            setGroups(groupRes.data.groups || []);
            setAllUsers((usersRes.data.users || []).filter(u => u._id !== currentUser._id && !u.phone?.startsWith('widget_')));
        } catch (error) { showToast(`Error al cargar contactos: ${friendlyError(error)}`); }
    };

    const handleToggleForwardTarget = (id) => {
        setForwardTargets(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleSendForward = async () => {
    if (!forwardModal || forwardTargets.length === 0) return;

    // Snapshot ANTES de cerrar el modal: si esperamos al await, el modal
    // queda visible varios segundos (especialmente en imagen/vídeo que
    // suben blob) y el usuario pulsa "Reenviar" varias veces creyendo que
    // no reaccionó → mensajes duplicados. Cerramos el modal al instante,
    // mostramos toast y procesamos en background.
    const msg = forwardModal;
    const targets = [...forwardTargets];
    setForwardModal(null);
    setForwardTargets([]);
    showToast(t.chat.sending || 'Enviando…', 'info');

    try {
        for (const targetId of targets) {
            // Backend manda grupos con _id (no id). El bug previo: con
            // groups.some(g => g.id === targetId) → siempre false → todo
            // se trataba como conversación directa, perdiendo el reenvío
            // a grupo aunque sí lo hubiera marcado el usuario.
            const isGroup = groups.some(g => (g._id || g.id) === targetId);
            const conv = !isGroup ? conversations.find(c => c.id === targetId) : null;
            const directUser = !isGroup && !conv ? allUsers.find(u => u._id === targetId) : null;
            const receiverId = conv?.otherUser?.id || directUser?._id;

            const myTranscription = msg.isOriginal
                ? (msg.transcription || '')
                : (msg.translatedTranscription || msg.transcription || '');
            const myText = msg.text || '';

            if (msg.mediaType === 'audio' && msg.mediaUrl) {
                await forwardAudio({
                    senderId: currentUser._id,
                    ...(isGroup ? { groupId: targetId } : { receiverId }),
                    audioUrl: msg.mediaUrl,
                    transcription: myTranscription,
                    originalLanguage: currentUser.language,
                    subtitlesUrl: msg.subtitlesUrl || '',
                    caption: myText
                });
            } else if (msg.mediaType === 'video' && msg.mediaUrl) {
                await forwardVideo({
                    senderId: currentUser._id,
                    ...(isGroup ? { groupId: targetId } : { receiverId }),
                    videoUrl: msg.mediaUrl,
                    transcription: myTranscription,
                    originalLanguage: currentUser.language,
                    subtitlesUrl: msg.subtitlesUrl || '',
                    caption: myText
                });
            } else if (msg.mediaType === 'image' && msg.mediaUrl) {
                const response = await fetch(msg.mediaUrl);
                const blob = await response.blob();
                const formData = new FormData();
                formData.append('image', blob, 'image.jpg');
                formData.append('senderId', currentUser._id);
                formData.append('caption', myText);
                if (isGroup) { formData.append('groupId', targetId); await sendGroupImage(formData); }
                else if (receiverId) { formData.append('receiverId', receiverId); await sendImage(formData); }
            } else {
                const text = myText || `📎 ${ui.common.forwardedFile}`;
                if (isGroup) { await sendGroupMessage({ groupId: targetId, senderId: currentUser._id, text }); }
                else if (receiverId) { await sendMessage({ senderId: currentUser._id, receiverId, text }); }
            }
        }
        showToast(t.chat.forwarded || 'Mensaje reenviado', 'success');
    } catch (error) { showToast(`Error al reenviar: ${friendlyError(error)}`); }
    // Refrescar lista de conversaciones para que aparezca el nuevo chat
    window.dispatchEvent(new Event('conversations_updated'));
};

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        // Al volver al final el usuario ya ve los mensajes pendientes.
        isAtBottomRef.current = true;
        setNewMessagesCount(0);
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
        if (!query.trim()) { setSearchResults([]); return; }
        const results = messages.reduce((acc, msg, index) => { if (msg.text?.toLowerCase().includes(query.toLowerCase())) acc.push(index); return acc; }, []);
        setSearchResults(results); setSearchIndex(0);
        if (results.length > 0) { const el = document.getElementById(`msg-${results[0]}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    };

    const handleSearchNext = () => {
        if (searchResults.length === 0) return;
        const next = (searchIndex + 1) % searchResults.length;
        setSearchIndex(next);
        const el = document.getElementById(`msg-${searchResults[next]}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const handleSearchPrev = () => {
        if (searchResults.length === 0) return;
        const prev = (searchIndex - 1 + searchResults.length) % searchResults.length;
        setSearchIndex(prev);
        const el = document.getElementById(`msg-${searchResults[prev]}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const isAdminUser = conversation?.type !== 'group' ? false : conversation?.admins?.some(a => (a?._id || a)?.toString() === currentUser._id?.toString());

    const handleBlock = async () => {
        try {
            if (isBlocked) { await unblockUser({ userId: currentUser._id, targetId: conversation.otherUser.id }); setIsBlocked(false); }
            else { await blockUser({ userId: currentUser._id, targetId: conversation.otherUser.id }); setIsBlocked(true); }
        } catch (err) { showToast(`Error al bloquear: ${friendlyError(err)}`); }
        setShowMenu(false);
    };

    const handleMute = async () => {
        try {
            const isGroup = conversation.type === 'group';
            const data = { userId: currentUser._id };
            if (isGroup) data.groupId = conversation.id; else data.conversationId = conversation.id;
            if (isMuted) { await unmuteConversation(data); setIsMuted(false); } else { await muteConversation(data); setIsMuted(true); }
        } catch (err) { showToast(`Error al silenciar: ${friendlyError(err)}`); }
        setShowMenu(false);
    };

    const handleFavorite = async () => {
        setShowMenu(false);  // cierra menú al instante para feedback visual
        try {
            const isGroup = conversation.type === 'group';
            const data = { userId: currentUser._id };
            if (isGroup) data.groupId = conversation.id; else data.conversationId = conversation.id;
            if (isFavorite) {
                await removeFavorite(data);
                setIsFavorite(false);
                showToast(t.chat.favRemoved || 'Eliminado de favoritos', 'success');
            } else {
                await addFavorite(data);
                setIsFavorite(true);
                showToast(t.chat.favAdded || 'Añadido a favoritos', 'success');
            }
            // Avisar al sidebar para que refresque la lista de conversaciones
            window.dispatchEvent(new Event('conversations_updated'));
        } catch (err) {
            showToast(`Error al añadir favorito: ${friendlyError(err)}`);
        }
    };

    const handleClearChat = async () => {
        if (!window.confirm(t.chat.clearChatConfirm)) return;
        try { await clearChat(conversation.id, { userId: currentUser._id }); setMessages([]); }
        catch (err) { showToast(`Error al vaciar chat: ${friendlyError(err)}`); }
        setShowMenu(false);
    };

    const handleDeleteConversation = async () => {
        if (!window.confirm(t.chat.deleteConvConfirm)) return;
        try { await deleteConversation(conversation.id, { userId: currentUser._id }); onBack(); }
        catch (err) { showToast(`Error al eliminar conversación: ${friendlyError(err)}`); }
        setShowMenu(false);
    };

    const handleLeaveGroup = async (newAdminId = null) => {
        try {
            const { leaveGroup } = await import('../../services/api');
            await leaveGroup({ groupId: conversation.id, userId: currentUser._id, newAdminId });
            setShowLeaveGroupModal(false); onBack();
            window.dispatchEvent(new Event('conversations_updated'));
        } catch (err) { showToast(`Error al salir del grupo: ${friendlyError(err)}`); }
    };

    const handleStartEdit = (msg) => {
        if (msg.sender?.id !== currentUser._id) return;
        if (msg.mediaType && msg.mediaType !== 'text') return;
        // Solo permite editar dentro de 15 min
        const age = Date.now() - new Date(msg.timestamp).getTime();
        if (age > 15 * 60 * 1000) { showToast('Ya no puedes editar este mensaje'); return; }
        setEditingMessage(msg);
        setNewMessage(msg.text || msg.originalText || '');
        setReplyingTo(null);
    };

    const handleCancelEdit = () => {
        setEditingMessage(null);
        setNewMessage('');
    };

    // Reintentar el envío de un mensaje que falló (status === 'failed').
    // Reutiliza el mismo tempId y hace el send de nuevo. Si vuelve a
    // fallar se queda en failed; si funciona, se actualiza con el ID real.
    const handleRetrySend = async (failedMsg) => {
        if (!failedMsg) return;
        const tempId = failedMsg.tempId || failedMsg.id;
        const text = failedMsg.text;
        if (!text) return;
        const replyPayload = {
            replyTo: failedMsg.replyTo || null,
            replyToText: failedMsg.replyToText || null,
            replyToSender: failedMsg.replyToSender || null,
            replyToMediaType: failedMsg.replyToMediaType || 'text',
            replyToMediaUrl: failedMsg.replyToMediaUrl || null
        };
        // Volver a estado pending mientras reintenta
        setMessages(prev => prev.map(m =>
            (m.tempId === tempId || m.id === tempId)
                ? { ...m, status: 'sending', isPending: true }
                : m
        ));
        try {
            let response;
            if (conversation.type === 'group') {
                response = await sendGroupMessage({ groupId: conversation.id, senderId: currentUser._id, text, ...replyPayload });
            } else {
                response = await sendMessage({ senderId: currentUser._id, receiverId: conversation.otherUser.id, text, ...replyPayload });
                if (!conversation.id && response.data?.data?.conversationId) {
                    conversation.id = response.data.data.conversationId.toString();
                    joinConversation(conversation.id);
                }
            }
            const realId = response.data?.data?.messageId;
            const realStatus = response.data?.data?.status || 'sent';
            setMessages(prev => prev.map(m =>
                (m.tempId === tempId || m.id === tempId)
                    ? { ...m, id: realId || m.id, status: realStatus, isPending: false }
                    : m
            ));
        } catch (error) {
            setMessages(prev => prev.map(m =>
                (m.tempId === tempId || m.id === tempId)
                    ? { ...m, status: 'failed', isPending: false }
                    : m
            ));
            showToast(`Error al reenviar: ${friendlyError(error)}`);
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();
        const original = newMessage.trim();
        if (!original) return;

        // Si estamos editando, mantener el flow original (no es optimistic
        // y no auto-corregimos: estamos editando un mensaje existente, no
        // queremos sorpresas).
        if (editingMessage) {
            try {
                await editMessage(editingMessage.id, { userId: currentUser._id, newText: original });
                setEditingMessage(null);
                setNewMessage('');
            } catch (error) {
                showToast(`Error al editar: ${friendlyError(error)}`);
            }
            return;
        }

        // ── AUTO-CORRECCIÓN antes de enviar ─────────────────────────────
        // Solo se aplica si el mensaje es lo bastante "frase" para que la
        // corrección tenga sentido (>=5 chars, al menos un espacio). Una
        // palabra suelta como "Kaixo" o "ok" se envía intacta.
        // Timeout de 2.5s — si la API tarda más, enviamos el original.
        let text = original;
        let wasCorrected = false;
        const eligibleForCorrection = original.length >= 5 && /\s/.test(original);
        if (eligibleForCorrection) {
            try {
                const result = await Promise.race([
                    correctText(original, currentUser.language),
                    new Promise(resolve => setTimeout(() => resolve(null), 2500))
                ]);
                if (result?.corrected && result?.changed) {
                    text = result.corrected;
                    wasCorrected = true;
                }
            } catch {
                // Si falla, enviamos el original sin error visible
            }
        }

        // ── ENVÍO OPTIMISTA estilo WhatsApp ─────────────────────────────
        const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const replyPayload = replyingTo ? {
            replyTo: replyingTo.id || null,
            replyToText: replyingTo.text || null,
            replyToSender: replyingTo.senderName || null,
            replyToMediaType: replyingTo.mediaType || 'text',
            replyToMediaUrl: replyingTo.mediaUrl || null
        } : { replyToText: null, replyToSender: null, replyToMediaType: null, replyToMediaUrl: null };

        // Burbuja optimistic en UI inmediatamente
        const optimisticMessage = {
            id: tempId,
            tempId,
            text,
            language: currentUser.language,
            isOriginal: true,
            ...replyPayload,
            sender: { id: currentUser._id, name: currentUser.name, avatar: currentUser.avatar, voice: currentUser.voice || 'female' },
            timestamp: new Date().toISOString(),
            status: 'sending',
            isPending: true
        };
        setMessages(prev => [...prev, optimisticMessage]);
        setNewMessage('');           // ← input limpio AL INSTANTE
        setReplyingTo(null);

        // Si la auto-corrección cambió algo, avisamos discretamente al
        // usuario. Si no cambió nada, ningún toast molesto.
        if (wasCorrected) {
            try { showToast(`✓ ${t.chat.correct?.changed || 'Corregido'}`, 'success'); } catch {}
        }

        // Envío real en background — la UI no espera
        try {
            let response;
            if (conversation.type === 'group') {
                response = await sendGroupMessage({ groupId: conversation.id, senderId: currentUser._id, text, ...replyPayload });
            } else {
                response = await sendMessage({ senderId: currentUser._id, receiverId: conversation.otherUser.id, text, ...replyPayload });
                // Si era una conversación nueva, fijar el id devuelto por backend
                if (!conversation.id && response.data?.data?.conversationId) {
                    conversation.id = response.data.data.conversationId.toString();
                    joinConversation(conversation.id);
                }
            }
            const realId = response.data?.data?.messageId;
            const realStatus = response.data?.data?.status || 'sent';
            // La response del backend ahora trae translatedText / receiverLanguage
            // ya resueltos (la traducción se hace antes del save). Usamos esos
            // datos directamente en lugar de depender del socket
            // 'message_translated' que puede llegar tarde o perderse en
            // reconexiones — el síntoma era que el emisor solo veía su idioma
            // hasta salir y volver a entrar.
            const realTranslatedText = response.data?.data?.translatedText;
            const realReceiverLang = response.data?.data?.receiverLanguage;
            setMessages(prev => prev.map(m =>
                m.tempId === tempId
                    ? {
                        ...m,
                        id: realId || m.id,
                        status: realStatus,
                        isPending: false,
                        translatedText: realTranslatedText !== undefined ? realTranslatedText : m.translatedText,
                        receiverLanguage: realReceiverLang || m.receiverLanguage
                    }
                    : m
            ));
        } catch (error) {
            // Marcar el optimistic como fallido para que el usuario sepa
            setMessages(prev => prev.map(m =>
                m.tempId === tempId
                    ? { ...m, status: 'failed', isPending: false }
                    : m
            ));
            showToast(`Error al enviar mensaje: ${friendlyError(error)}`);
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { showToast('Archivo no válido: debe ser una imagen'); return; }
        if (file.size > 10 * 1024 * 1024) { showToast('Imagen demasiado grande (máx 10MB)'); return; }
        setSelectedImage(file);
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
    };

    const handleCancelImage = () => { setSelectedImage(null); setImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; };

    // Compartir un contacto KAIXO en el chat (estilo WhatsApp). El selectedContact
    // viene del ContactPickerModal y se envía como contactCard al backend.
    // Igual flow para 1:1 y grupos — sendMessage / sendGroupMessage aceptan
    // el campo opcional contactCard. El backend ENRIQUECE phone/avatar/etc
    // desde DB (el cliente no siempre tiene phone por privacidad), así que
    // devuelve la versión final que añadimos optimista al chat.
    const handleSendContact = async (selectedContact) => {
        setShowContactPicker(false);
        setShowAttachMenu(false);
        if (!selectedContact?.userId) return;
        const previewText = `👤 ${selectedContact.name || 'Contacto'}`;
        try {
            let response;
            if (conversation.type === 'group') {
                response = await sendGroupMessage({
                    groupId: conversation.id,
                    senderId: currentUser._id,
                    text: previewText,
                    contactCard: selectedContact
                });
            } else if (conversation.otherUser?.id) {
                response = await sendMessage({
                    senderId: currentUser._id,
                    receiverId: conversation.otherUser.id,
                    text: previewText,
                    contactCard: selectedContact
                });
            } else { return; }

            // Optimistic-add para que el EMISOR vea la burbuja al instante.
            // Tomamos los datos enriquecidos del backend (phone real, etc).
            const d = response?.data?.data || {};
            const realCard = d.contactCard || selectedContact;
            const messageId = d.messageId;
            if (messageId) {
                setMessages(prev => [...prev, {
                    id: messageId,
                    text: previewText,
                    language: currentUser.language,
                    isOriginal: true,
                    mediaType: 'contact',
                    contactCard: realCard,
                    sender: { id: currentUser._id, name: currentUser.name, avatar: currentUser.avatar, voice: currentUser.voice || 'female' },
                    timestamp: new Date(),
                    status: 'sent'
                }]);
            }
        } catch (err) {
            showToast(`Error al compartir contacto: ${friendlyError(err)}`);
        }
    };

    // Compartir un canal de Latidos en el chat. El selectedChannel viene
    // del ChannelPickerModal y se envía como channelCard al backend, que
    // lo enriquece desde DB (ChannelV2). En el chat aparece una burbuja
    // con el canal y un botón "Seguir" que el receptor puede pulsar.
    const handleSendChannel = async (selectedChannel) => {
        setShowChannelPicker(false);
        setShowAttachMenu(false);
        if (!selectedChannel?.channelId) return;
        const previewText = `📡 ${selectedChannel.name || 'Canal de Latidos'}`;
        try {
            let response;
            if (conversation.type === 'group') {
                response = await sendGroupMessage({
                    groupId: conversation.id,
                    senderId: currentUser._id,
                    text: previewText,
                    channelCard: selectedChannel
                });
            } else if (conversation.otherUser?.id) {
                response = await sendMessage({
                    senderId: currentUser._id,
                    receiverId: conversation.otherUser.id,
                    text: previewText,
                    channelCard: selectedChannel
                });
            } else { return; }
            const d = response?.data?.data || {};
            const realCard = d.channelCard || selectedChannel;
            const messageId = d.messageId;
            if (messageId) {
                setMessages(prev => [...prev, {
                    id: messageId,
                    text: previewText,
                    language: currentUser.language,
                    isOriginal: true,
                    mediaType: 'channel',
                    channelCard: realCard,
                    sender: { id: currentUser._id, name: currentUser.name, avatar: currentUser.avatar, voice: currentUser.voice || 'female' },
                    timestamp: new Date(),
                    status: 'sent'
                }]);
            }
        } catch (err) {
            showToast(`Error al compartir canal: ${friendlyError(err)}`);
        }
    };

    // Seguir un canal desde una invitación recibida. Llama al endpoint
    // v2/subscribe y marca el canal como seguido localmente para que el
    // botón pase a "Siguiendo ✓" sin recargar.
    const handleFollowChannel = async (cc) => {
        if (!cc?.channelId) return;
        try {
            await v2Subscribe({ channelId: cc.channelId });
            setFollowedChannelsLocal(prev => {
                const next = new Set(prev);
                next.add(cc.channelId.toString());
                return next;
            });
            showToast(`✓ Sigues a ${cc.name || 'el canal'}`, 'success');
        } catch (err) {
            showToast(`No se pudo seguir: ${friendlyError(err)}`);
        }
    };

    // Añadir un contactCard a la agenda nativa del móvil. Solo funciona
    // en Capacitor (Android/iOS). En web mostramos un toast.
    const handleAddCardToPhoneContacts = async (cc) => {
        if (!cc) return;
        if (!cc.phone) {
            showToast(t.chat.contactCard.errNoPhone, 'error');
            return;
        }
        let ContactsPlugin = null;
        try { ContactsPlugin = require('@capacitor-community/contacts').Contacts; } catch {}
        if (!ContactsPlugin?.createContact) {
            showToast(t.chat.contactCard.errMobileOnly, 'error');
            return;
        }
        try {
            try {
                if (ContactsPlugin.requestPermissions) {
                    const req = await ContactsPlugin.requestPermissions();
                    if (req?.contacts !== 'granted') {
                        showToast(t.chat.contactCard.errPermDenied, 'error');
                        return;
                    }
                }
            } catch (permErr) {
                console.warn('[contactCard] requestPermissions:', permErr?.message);
            }

            const phoneNum = String(cc.phone).trim();
            const contact = {
                name: { given: cc.name || t.chat.contactCard.fallbackName },
                phones: [{ number: phoneNum, isPrimary: true, type: 'mobile', label: 'KAIXO' }]
            };
            if (cc.email) {
                contact.emails = [{ address: cc.email, type: 'home' }];
            }
            const noteParts = [];
            if (cc.handle) noteParts.push(`@${cc.handle}`);
            if (cc.language) noteParts.push(cc.language);
            const place = [cc.region, cc.country].filter(Boolean).join(', ');
            if (place) noteParts.push(place);
            if (noteParts.length) contact.note = `KAIXO · ${noteParts.join(' · ')}`;

            await ContactsPlugin.createContact({ contact });

            showToast(`✓ ${cc.name || t.chat.contactCard.fallbackName} ${t.chat.contactCard.savedSuffix}`, 'success');
            try { window.dispatchEvent(new CustomEvent('kaixo_contacts_changed')); } catch {}
        } catch (e) {
            console.error('[contactCard] createContact error:', e);
            showToast(`${t.chat.contactCard.errSavePrefix}: ${e?.message || 'error'}`, 'error');
        }
    };

    const handleSendImage = async () => {
        if (!selectedImage) return;
        setUploading(true);
        const formData = new FormData();
        formData.append('image', selectedImage);
        formData.append('senderId', currentUser._id);
        formData.append('caption', newMessage || '');
        if (conversation.type === 'group') formData.append('groupId', conversation.id);
        else formData.append('receiverId', conversation.otherUser.id);
        try {
            if (conversation.type === 'group') await sendGroupImage(formData); else await sendImage(formData);
            setSelectedImage(null); setImagePreview(null); setNewMessage('');
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (error) { showToast(`Error al enviar imagen: ${friendlyError(error)}`); }
        setUploading(false);
    };

    const handleMediaDownload = async (url, kind) => {
        if (!url) return;
        const r = await downloadMedia(url, kind);
        showToast(r.ok
            ? (kind === 'video' ? '✅ Video guardado' : '✅ Imagen guardada')
            : `❌ ${r.error || 'Error al descargar'}`
        );
    };

    const handleVideoSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('video/')) { showToast('Archivo no válido: debe ser un video'); return; }
        if (file.size > 100 * 1024 * 1024) { showToast('Video demasiado grande (máx 100MB)'); return; }
        setSelectedVideo(file); setVideoPreview(URL.createObjectURL(file));
    };

    const handleCancelVideo = () => { setSelectedVideo(null); setVideoPreview(null); setVideoPreviewPoster(null); if (videoInputRef.current) videoInputRef.current.value = ''; };

    // Genera una miniatura del primer frame del video local. WebViews
    // antiguos (Huawei P30) ignoran preload="metadata" y dejan el preview
    // negro hasta que el usuario pulsa play. Capturamos el frame a un
    // canvas y lo asignamos como poster para que se vea la miniatura
    // siempre — sin depender de la cooperación del WebView.
    useEffect(() => {
        if (!videoPreview) { setVideoPreviewPoster(null); return; }
        let canceled = false;
        const v = document.createElement('video');
        v.muted = true; v.playsInline = true; v.preload = 'auto';
        v.src = videoPreview;
        const cleanup = () => { try { v.src = ''; v.load?.(); } catch {} };
        v.onloadedmetadata = () => {
            try { v.currentTime = Math.min(0.1, (v.duration || 1) - 0.01); } catch {}
        };
        v.onseeked = () => {
            if (canceled) return;
            try {
                const w = v.videoWidth || 320, h = v.videoHeight || 240;
                if (!w || !h) { cleanup(); return; }
                const max = 480;
                const scale = Math.min(1, max / Math.max(w, h));
                const cw = Math.max(1, Math.round(w * scale));
                const ch = Math.max(1, Math.round(h * scale));
                const c = document.createElement('canvas');
                c.width = cw; c.height = ch;
                const ctx = c.getContext('2d');
                ctx.drawImage(v, 0, 0, cw, ch);
                setVideoPreviewPoster(c.toDataURL('image/jpeg', 0.7));
            } catch {}
            cleanup();
        };
        v.onerror = cleanup;
        return () => { canceled = true; cleanup(); };
    }, [videoPreview]);

    const handleStartRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];
            setRecordingSeconds(0);
            mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
            mediaRecorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                setAudioBlob(blob); setAudioPreview(URL.createObjectURL(blob));
                stream.getTracks().forEach(track => track.stop());
            };
            mediaRecorder.start(); setIsRecording(true);
            recordingTimerRef.current = setInterval(() => setRecordingSeconds(prev => prev + 1), 1000);
        } catch (err) { showToast(`Sin acceso al micrófono: ${err.message}`); }
    };

    const handleStopRecording = () => {
        if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false); clearInterval(recordingTimerRef.current); }
    };

    const handleCancelAudio = () => { setAudioPreview(null); setAudioBlob(null); setRecordingSeconds(0); };

    const handleAudioFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setAudioBlob(file); setAudioPreview(URL.createObjectURL(file));
    };

    const handleSendAudio = async () => {
        if (!audioBlob || uploading) return;
        setUploading(true);
        try {
            const formData = new FormData();
            const isGroup = conversation.type === 'group';
            formData.append('audio', audioBlob, audioBlob.name || 'audio.webm');
            if (isGroup) { formData.append('groupId', conversation.id); formData.append('senderId', currentUser._id); }
            else { formData.append('senderId', currentUser._id); formData.append('receiverId', conversation.otherUser.id); }
            isGroup ? await sendGroupAudio(formData) : await sendAudio(formData);
            setAudioPreview(null); setAudioBlob(null); setRecordingSeconds(0);
        } catch (err) { showToast(`Error al enviar audio: ${friendlyError(err)}`); }
        finally { setUploading(false); }
    };

    const handleDocumentSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setDocumentFile(file); setDocumentPreview(file.name);
    };

    const handleCancelDocument = () => { setDocumentFile(null); setDocumentPreview(null); if (documentInputRef.current) documentInputRef.current.value = ''; };

    const getFileIcon = (fileName) => {
        const ext = fileName?.split('.').pop()?.toLowerCase();
        const icons = { pdf: '📕', docx: '📘', doc: '📘', xlsx: '📗', xls: '📗', pptx: '📙', ppt: '📙', txt: '📄' };
        return icons[ext] || '📄';
    };

    const handleSendDocument = async () => {
        if (!documentFile || uploading) return;
        setUploading(true);
        try {
            const formData = new FormData();
            const isGroup = conversation.type === 'group';
            formData.append('document', documentFile, documentFile.name);
            formData.append('senderId', currentUser._id);
            if (isGroup) formData.append('groupId', conversation.id); else formData.append('receiverId', conversation.otherUser.id);
            isGroup ? await sendGroupDocument(formData) : await sendDocument(formData);
            setDocumentFile(null); setDocumentPreview(null);
            if (documentInputRef.current) documentInputRef.current.value = '';
        } catch (err) { showToast(`Error al enviar documento: ${friendlyError(err)}`); }
        finally { setUploading(false); }
    };

    const handleSendVideo = async () => {
        if (!selectedVideo) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('video', selectedVideo);
            formData.append('senderId', currentUser._id);
            formData.append('caption', newMessage || '');
            if (conversation.type === 'group') { formData.append('groupId', conversation.id); await sendGroupVideo(formData); }
            else { formData.append('receiverId', conversation.otherUser.id); await sendVideo(formData); }
            setSelectedVideo(null); setVideoPreview(null); setVideoPreviewPoster(null); setNewMessage('');
            if (videoInputRef.current) videoInputRef.current.value = '';
        } catch (error) { showToast(`Error al enviar video: ${friendlyError(error)}`); }
        setUploading(false);
    };

    const emitTypingEvent = () => {
        if (conversation.type === 'group') emitTyping({ groupId: conversation.id, userId: currentUser._id, userName: currentUser.name });
        else emitTyping({ conversationId: conversation.id, userId: currentUser._id, userName: currentUser.name });
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => {
            if (conversation.type === 'group') emitStopTyping({ groupId: conversation.id, userId: currentUser._id });
            else emitStopTyping({ conversationId: conversation.id, userId: currentUser._id });
        }, 2000);
    };

    const handleOpenCamera = () => {
    setShowCamera(true);
    setShowAttachMenu(false);
};
        
    // Quitar emojis, símbolos y caracteres especiales antes de hablar
    const stripEmojis = (str) => str
        .replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2702}-\u{27B0}\u{200D}\u{20E3}\u{FE0F}]/gu, '')
        .replace(/[━─│┌┐└┘├┤┬┴┼╔╗╚╝╠╣╦╩╬]/g, '')
        .replace(/📢|📌|👤|📞|✉️|📎|🎙️|📷|🖼️|📄|🎥|🌐|👥|🔗|💬|📝|⭐|🏪|〰️/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();

    const speakMessage = async (text, language, senderVoice) => {
    if (!text) return;
    const cleanText = stripEmojis(text);
    if (!cleanText) return;
    const langMapBCP = {
        'español': 'es-ES', 'inglés': 'en-US', 'francés': 'fr-FR',
        'alemán': 'de-DE', 'portugués': 'pt-BR', 'italiano': 'it-IT',
        'euskera': 'es-ES', 'chino': 'zh-CN', 'japonés': 'ja-JP',
        'árabe': 'ar-SA', 'hindi': 'hi-IN', 'ruso': 'ru-RU',
        'coreano': 'ko-KR', 'turco': 'tr-TR', 'indonesio': 'id-ID'
    };
    if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(cleanText);
        const bcp = langMapBCP[language] || 'es-ES';
        utterance.lang = bcp;
        // Buscar voz exacta por nombre (guardada en perfil del usuario)
        if (senderVoice && senderVoice !== 'male' && senderVoice !== 'female') {
            const exactVoice = availableVoices.find(v => v.name === senderVoice);
            if (exactVoice) utterance.voice = exactVoice;
        } else {
            // Fallback para voces antiguas (male/female)
            const prefix = bcp.split('-')[0];
            const langVoices = availableVoices.filter(v => v.lang.toLowerCase().startsWith(prefix));
            if (langVoices.length > 0) {
                utterance.voice = langVoices[senderVoice === 'male' ? langVoices.length - 1 : 0];
            }
            utterance.pitch = senderVoice === 'male' ? 0.7 : 1.2;
        }
        utterance.rate = 1.0;
        window.speechSynthesis.speak(utterance);
    } else {
        try {
            await TextToSpeech.speak({
                text: cleanText, lang: langMapBCP[language] || 'es-ES',
                rate: 1.0, pitch: senderVoice === 'male' ? 0.7 : 1.2,
                volume: 1.0, category: 'ambient'
            });
        } catch(e) { showToast(`Error TTS: ${friendlyError(e)}`); }
    }
};

    // ── TTS genérico con pause/stop para texto, audio y video ──────────
    const handleTtsPlay = (msgId, text, language, senderVoice, videoId) => {
        if (!text) return;
        // Si es video, mutear el audio del video
        if (videoId) {
            const videoEl = document.getElementById(`video-${videoId}`);
            if (videoEl) videoEl.muted = true;
        }
        setActiveTtsId(msgId);
        setTtsPausedId(null);
        speakMessage(text, language, senderVoice);
        // Detectar fin de la síntesis. Guardamos el interval en ref para
        // poder limpiarlo si el componente se desmonta mientras suena.
        if (window.speechSynthesis) {
            if (ttsCheckIntervalRef.current) clearInterval(ttsCheckIntervalRef.current);
            ttsCheckIntervalRef.current = setInterval(() => {
                if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
                    clearInterval(ttsCheckIntervalRef.current);
                    ttsCheckIntervalRef.current = null;
                    setActiveTtsId(prev => prev === msgId ? null : prev);
                    setTtsPausedId(prev => prev === msgId ? null : prev);
                }
            }, 200);
        }
    };

    const handleTtsPause = async (msgId) => {
        if (window.speechSynthesis) window.speechSynthesis.pause();
        else await TextToSpeech.stop().catch(() => {});
        setTtsPausedId(msgId);
        setActiveTtsId(null);
    };

    const handleTtsResume = (msgId) => {
        if (window.speechSynthesis) window.speechSynthesis.resume();
        setActiveTtsId(msgId);
        setTtsPausedId(null);
    };

    const handleTtsStop = async (msgId) => {
        if (window.speechSynthesis) window.speechSynthesis.cancel();
        else await TextToSpeech.stop().catch(() => {});
        setActiveTtsId(null);
        setTtsPausedId(null);
    };


    // Determina si emisor y receptor comparten idioma (ocultar TTS/transcripción)
    const isSameLanguage = (msg) => {
        // En grupos, antes devolvíamos false sin más, lo que provocaba que
        // los TTS y la transcripción tradujera/leyera mensajes aunque emisor
        // y lector compartieran idioma. Ahora comparamos el idioma del
        // emisor con el del lector (currentUser) — coincidir = no hay nada
        // que traducir/leer y se oculta TTS + transcripción duplicada.
        if (conversation?.type === 'group') {
            const sLang = msg.originalLanguage || (msg.isOriginal ? msg.language : null);
            const myLang = currentUser?.language;
            return sLang && myLang && sLang === myLang;
        }
        const sLang = msg.originalLanguage || (msg.isOriginal ? msg.language : msg.originalLanguage);
        const rLang = msg.isOriginal ? (msg.receiverLanguage || conversation?.otherUser?.language) : msg.language;
        return sLang && rLang && sLang === rLang;
    };

    if (!conversation) {
        return (
            <div className="chat-window-empty">
                <div className="empty-state">
                    <h2>🌍 KAIXO</h2>
                    <p>{t.chat.selectConversation}</p>
                </div>
            </div>
        );
    }

    // Chat con KAIXO Support: reemplazamos todo el flujo del chat tradicional
    // por el panel SupportChat, que ya incluye el historial arriba + menú de
    // FAQ + input libre. Mantiene la interfaz limpia sin mezclar botones.
    if (isSupportChat) {
        return (
            <div className="chat-window">
                <div className="chat-header">
                    <button className="back-btn" onClick={onBack}>
                        <Icons.ArrowBack color="white" size={22} />
                    </button>
                    <div className="chat-user-avatar">
                        {conversation.otherUser?.avatar
                            ? <img src={conversation.otherUser.avatar} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                            : 'K'}
                    </div>
                    <div className="chat-user-info">
                        <div className="chat-user-name">KAIXO Support</div>
                        <div className="chat-user-language">Online 24/7</div>
                    </div>
                </div>
                <SupportChat currentUser={currentUser} />
            </div>
        );
    }

    return (
        <div className="chat-window">
            {selectionMode && (
                <div className="chat-selection-bar">
                    <button
                        className="chat-selection-btn"
                        onClick={exitSelection}
                        aria-label="Cancelar selección"
                        title="Cancelar"
                    >
                        ✕
                    </button>
                    <span className="chat-selection-count">
                        {selectedMessageIds.size} {selectedMessageIds.size === 1 ? 'seleccionado' : 'seleccionados'}
                    </span>
                    <button
                        className="chat-selection-btn chat-selection-delete"
                        onClick={() => { if (selectedMessageIds.size > 0) setBulkDeleteModal(true); }}
                        disabled={selectedMessageIds.size === 0}
                        aria-label="Eliminar mensajes seleccionados"
                        title="Eliminar"
                    >
                        🗑
                    </button>
                </div>
            )}
            <div className="chat-header" style={selectionMode ? { display: 'none' } : undefined}>
                <button className="back-btn" onClick={onBack}>
                    <Icons.ArrowBack color="white" size={22} />
                </button>
                <div
                    className="chat-user-avatar"
                    style={{ position: 'relative', cursor: (conversation.type === 'group' ? conversation.avatar : conversation.otherUser.avatar) ? 'pointer' : 'default' }}
                    onClick={() => {
                        const url = conversation.type === 'group' ? conversation.avatar : conversation.otherUser.avatar;
                        if (url) setImageViewer(url);
                    }}
                >
                    {conversation.type === 'group'
                        ? (conversation.avatar ? <img src={conversation.avatar} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : conversation.name?.charAt(0).toUpperCase())
                        : (conversation.otherUser.avatar ? <img src={conversation.otherUser.avatar} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : conversation.otherUser.name.charAt(0))
                    }
                    {conversation.type !== 'group' && onlineUsers?.has(conversation.otherUser.id) && <span className="online-dot-header" />}
                </div>
                <div
                    className="chat-user-info"
                    onClick={() => { if (conversation.type !== 'group') setShowUserInfo(true); }}
                    style={conversation.type !== 'group' ? { cursor: 'pointer' } : {}}
                >
                    <div className="chat-user-name">{conversation.type === 'group' ? conversation.name : conversation.otherUser.name}</div>
                    <div className="chat-user-language">
                        {conversation.type === 'group'
                            ? `${conversation.members.length} ${t.chat.members}`
                            : onlineUsers?.has(conversation.otherUser.id)
                                ? `🟢 ${t.chat.online}${conversation.otherUser.status ? ` · ${conversation.otherUser.status}` : ''}`
                                : conversation.otherUser.status || `${conversation.otherUser.language} - ${conversation.otherUser.region}`
                        }
                    </div>
                </div>
                <div className="header-actions" ref={menuRef}>
                    {/* Botones de llamar. En 1-a-1 llaman directo al otro user.
                        En grupos llaman a TODOS los miembros (modo grupal). */}
                    {conversation.type !== 'group' && conversation.otherUser?.id && (
                        <>
                            <button
                                className="header-icon-btn"
                                onClick={() => window.dispatchEvent(new CustomEvent('kaixo:call:start', {
                                    detail: {
                                        calleeId: conversation.otherUser.id,
                                        type: 'voice',
                                        name: conversation.otherUser.name,
                                        avatar: conversation.otherUser.avatar
                                    }
                                }))}
                                aria-label="Llamada de voz"
                                title="Llamar"
                            >
                                <Icons.Phone size={22} color="white" />
                            </button>
                            <button
                                className="header-icon-btn"
                                onClick={() => window.dispatchEvent(new CustomEvent('kaixo:call:start', {
                                    detail: {
                                        calleeId: conversation.otherUser.id,
                                        type: 'video',
                                        name: conversation.otherUser.name,
                                        avatar: conversation.otherUser.avatar
                                    }
                                }))}
                                aria-label="Videollamada"
                                title="Videollamada"
                            >
                                <Icons.VideoCamera size={22} color="white" />
                            </button>
                        </>
                    )}
                    {conversation.type === 'group' && conversation.id && (
                        <>
                            <button
                                className="header-icon-btn"
                                onClick={() => window.dispatchEvent(new CustomEvent('kaixo:group_call_start', {
                                    detail: {
                                        groupId: conversation.id,
                                        groupName: conversation.name,
                                        groupAvatar: conversation.avatar || '',
                                        type: 'voice'
                                    }
                                }))}
                                aria-label="Llamada de voz grupal"
                                title="Llamar al grupo"
                            >
                                <Icons.Phone size={22} color="white" />
                            </button>
                            <button
                                className="header-icon-btn"
                                onClick={() => window.dispatchEvent(new CustomEvent('kaixo:group_call_start', {
                                    detail: {
                                        groupId: conversation.id,
                                        groupName: conversation.name,
                                        groupAvatar: conversation.avatar || '',
                                        type: 'video'
                                    }
                                }))}
                                aria-label="Videollamada grupal"
                                title="Videollamar al grupo"
                            >
                                <Icons.VideoCamera size={22} color="white" />
                            </button>
                        </>
                    )}
                    <button className="header-icon-btn" onClick={() => setShowMenu(!showMenu)}>
                        <Icons.More color="white" size={22} />
                    </button>
                    {showMenu && (
                        <div className="header-menu">
                            {conversation.type === 'group' ? (
                                <>
                                    <div className="menu-item" onClick={() => { setSearchMode(!searchMode); setShowMenu(false); setSearchQuery(''); setSearchResults([]); }}><Icons.Search size={16} /> {t.chat.searchMenu.replace('🔍 ', '')}</div>
                                    {/* Ajustes — entrada principal a la gestión del grupo.
                                        Antes había accesos directos a "Añadir miembro" y
                                        "Bloqueados" pero estaban duplicados (también dentro
                                        del modal de ajustes). Ahora solo "Ajustes". */}
                                    <div className="menu-item" onClick={() => { setGroupSettingsTab('members'); setShowGroupSettings(true); setShowMenu(false); }}>
                                        <Icons.Settings size={16} /> {t.chat.groupSettings || 'Ajustes'}
                                    </div>
                                    <div className="menu-item" onClick={handleMute}>{isMuted ? t.chat.unmuteNotif : t.chat.muteNotif}</div>
                                    <div className="menu-item" onClick={handleFavorite}>{isFavorite ? t.chat.removeFav : t.chat.addFav}</div>
                                    <div className="menu-item menu-item-danger" onClick={handleClearChat}><Icons.Trash size={16} color="#e53e3e" /> {t.chat.clearChat.replace('🗑️ ', '')}</div>
                                    <div className="menu-item menu-item-danger" onClick={() => { setShowLeaveGroupModal(true); setShowMenu(false); }}>🚪 {t.chat.leaveGroupMenu.replace('🚪 ', '')}</div>
                                    {isAdminUser && (
                                        <div className="menu-item menu-item-danger" onClick={() => { setShowMenu(false); handleDeleteGroup(); }}>
                                            <Icons.Trash size={16} color="#e53e3e" /> Eliminar grupo
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="menu-item" onClick={() => { setSearchMode(!searchMode); setShowMenu(false); setSearchQuery(''); setSearchResults([]); }}><Icons.Search size={16} /> {t.chat.searchMenu.replace('🔍 ', '')}</div>
                                    <div className="menu-item" onClick={() => { setShowUserInfo(true); setShowMenu(false); }}>ℹ️ {t.chat.contactInfo.replace('ℹ️ ', '')}</div>
                                    <div className="menu-item" onClick={handleMute}>{isMuted ? t.chat.unmuteNotif : t.chat.muteNotif}</div>
                                    <div className="menu-item" onClick={handleFavorite}>{isFavorite ? t.chat.removeFav : t.chat.addFav}</div>
                                    <div className="menu-item menu-item-danger" onClick={handleBlock}>{isBlocked ? t.chat.unblock : t.chat.block}</div>
                                    <div className="menu-item menu-item-danger" onClick={handleClearChat}><Icons.Trash size={16} color="#e53e3e" /> {t.chat.clearChat.replace('🗑️ ', '')}</div>
                                    <div className="menu-item menu-item-danger" onClick={handleDeleteConversation}><Icons.Close size={16} color="#e53e3e" /> {t.chat.deleteChat.replace('❌ ', '')}</div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {searchMode && (
                <div className="search-bar">
                    <Icons.Search size={16} color="var(--text-secondary)" />
                    <input type="text" className="search-input" placeholder={t.chat.searchPlaceholder} value={searchQuery} onChange={(e) => handleSearch(e.target.value)} autoFocus />
                    {searchResults.length > 0 && <span className="search-count">{searchIndex + 1}/{searchResults.length}</span>}
                    <button className="search-nav-btn" onClick={handleSearchPrev}><Icons.ChevronUp size={14} /></button>
                    <button className="search-nav-btn" onClick={handleSearchNext}><Icons.ChevronDown size={14} /></button>
                    <button className="search-nav-btn" onClick={() => { setSearchMode(false); setSearchQuery(''); setSearchResults([]); }}><Icons.Close size={14} /></button>
                </div>
            )}

            {/* Banner de bienvenida al grupo: "Has sido añadido a este
                grupo por X" + Aceptar/Salir. Solo se muestra mientras el
                usuario no haya hecho acknowledge (campo en backend). */}
            {showGroupWelcome && (
                <div className="chat-group-welcome-banner">
                    <div className="chat-group-welcome-text">
                        🎉 Has sido añadido a este grupo
                        {conversation.addedByName ? <> por <strong>{conversation.addedByName}</strong></> : null}.
                    </div>
                    <div className="chat-group-welcome-actions">
                        <button className="chat-group-welcome-leave" onClick={handleGroupLeave}>
                            Salir
                        </button>
                        <button className="chat-group-welcome-accept" onClick={handleGroupAccept}>
                            Aceptar
                        </button>
                    </div>
                </div>
            )}

            {/* Banner: este remitente NO está en tu agenda. Dos acciones:
                Bloquear (corta el contacto) o Añadir a contactos (abre form). */}
            {!isGroup && conversation.senderNotInContacts && !strangerDismissed && !isBlocked && (
                <div className="chat-stranger-banner">
                    <div className="chat-stranger-text">
                        ⚠ <strong>{conversation.otherUser?.name || 'Esta persona'}</strong> no está en tus contactos.
                    </div>
                    <div className="chat-stranger-actions">
                        <button className="chat-stranger-btn chat-stranger-block" onClick={handleStrangerBlock}>
                            Bloquear
                        </button>
                        <button className="chat-stranger-btn chat-stranger-add" onClick={() => setShowAddContactForm(true)}>
                            + Añadir a contactos
                        </button>
                    </div>
                </div>
            )}

            {showAddContactForm && (
                <AddContactForm
                    initial={{
                        name: conversation.otherUser?.name || '',
                        phone: conversation.otherUser?.phone || '',
                        email: '',
                        notes: ''
                    }}
                    onCancel={() => setShowAddContactForm(false)}
                    onSaved={() => {
                        setShowAddContactForm(false);
                        setStrangerDismissed(true);
                        try { window.dispatchEvent(new CustomEvent('kaixo_contacts_changed')); } catch {}
                    }}
                />
            )}

            <div
                className="messages-container"
                ref={messagesContainerRef}
                onScroll={(e) => {
                    const el = e.currentTarget;
                    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
                    const atBottom = distanceFromBottom < 80;
                    isAtBottomRef.current = atBottom;
                    setShowScrollDown(distanceFromBottom > 200);
                    // Cuando el usuario alcanza el final manualmente, los
                    // mensajes nuevos ya están a la vista → resetear contador.
                    if (atBottom) setNewMessagesCount(0);
                }}
            >
                {loading ? (
                    <div className="loading">{t.chat.loading}</div>
                ) : (
                    <>
                        {hasMore && (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                                <button
                                    onClick={loadMoreMessages}
                                    disabled={loadingMore}
                                    style={{
                                        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                                        color: 'var(--text-secondary)', borderRadius: '16px',
                                        padding: '6px 16px', fontSize: '12px', cursor: loadingMore ? 'default' : 'pointer',
                                        opacity: loadingMore ? 0.5 : 1
                                    }}
                                >
                                    {loadingMore ? t.chat.loading : '↑ Cargar mensajes anteriores'}
                                </button>
                            </div>
                        )}
                        {messages.map((msg, index) => {
                            // Separador de día estilo WhatsApp: cuando el día del
                            // mensaje cambia respecto al anterior, insertamos una
                            // pill central con "Hoy" / "Ayer" / nombre del día / fecha.
                            let dayPill = null;
                            if (msg.timestamp) {
                                const prev = messages[index - 1];
                                const prevDate = prev?.timestamp ? new Date(prev.timestamp) : null;
                                const curDate = new Date(msg.timestamp);
                                const sameDay = prevDate
                                    && prevDate.getFullYear() === curDate.getFullYear()
                                    && prevDate.getMonth() === curDate.getMonth()
                                    && prevDate.getDate() === curDate.getDate();
                                if (!sameDay) {
                                    dayPill = (
                                        <div className="chat-day-separator">
                                            <span>{formatDayHeader(curDate, currentUser?.language)}</span>
                                        </div>
                                    );
                                }
                            }
                            return (
                              <React.Fragment key={`wrap-${msg.id}-${index}`}>
                                {dayPill}
                                {msg.isSystemMessage ? (
                            <div key={`${msg.id}-${index}`} id={`msg-${index}`} className="system-message-container">
                                <span className="system-message">{msg.text}</span>
                            </div>
                        ) : msg.mediaType === 'call' ? (
                            // Registro de llamada: pill centrado estilo WhatsApp
                            // con icono (teléfono o video) + estado + duración.
                            (() => {
                                const info = msg.call || {};
                                const isVideo = info.type === 'video';
                                const st = info.status;
                                const isSender = msg.sender?.id === currentUser._id;
                                const dur = info.durationSec || 0;
                                const fmtDur = (s) => {
                                    if (!s) return '';
                                    const m = Math.floor(s / 60);
                                    const ss = String(s % 60).padStart(2, '0');
                                    return `${m}:${ss}`;
                                };
                                const timeStr = new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                let label;
                                if (st === 'answered') {
                                    label = `${isVideo ? 'Videollamada' : 'Llamada de voz'} · ${fmtDur(dur)}`;
                                } else if (st === 'missed') {
                                    label = isSender
                                        ? `${isVideo ? 'Videollamada' : 'Llamada de voz'} no contestada`
                                        : `${isVideo ? 'Videollamada' : 'Llamada de voz'} perdida`;
                                } else if (st === 'declined') {
                                    label = `${isVideo ? 'Videollamada' : 'Llamada de voz'} rechazada`;
                                } else if (st === 'canceled') {
                                    label = `${isVideo ? 'Videollamada' : 'Llamada de voz'} cancelada`;
                                } else {
                                    label = isVideo ? 'Videollamada' : 'Llamada de voz';
                                }
                                const isNotAnswered = st === 'missed' || st === 'declined' || st === 'canceled';
                                return (
                                    <div key={`${msg.id}-${index}`} id={`msg-${index}`} className="call-log-container">
                                        <div className={`call-log-pill ${isNotAnswered ? 'call-log-missed' : ''}`}>
                                            <span className="call-log-icon" aria-hidden="true">
                                                {isVideo ? '📹' : '📞'}
                                            </span>
                                            <span className="call-log-text">{label}</span>
                                            <span className="call-log-time">{timeStr}</span>
                                        </div>
                                    </div>
                                );
                            })()
                        ) : (
                            <div key={`${msg.id}-${index}`} id={`msg-${index}`} className={`message ${msg.sender.id === currentUser._id ? 'sent' : 'received'} ${searchResults.includes(index) && searchResults[searchIndex] === index ? 'message-highlighted' : searchResults.includes(index) ? 'message-found' : ''}`}>
                                <div className="message-wrapper">
                                    {/* Avatar del autor (solo grupos, mensajes recibidos no de sistema).
                                        Click abre el perfil del autor. Si no hay foto, mostramos
                                        la primera letra del nombre con el color asignado. */}
                                    {conversation.type === 'group' && msg.sender?.id?.toString() !== currentUser._id?.toString() && !msg.isSystemMessage && (
                                        <div
                                            className="message-author-avatar"
                                            onClick={(e) => { e.stopPropagation(); openSenderProfile(msg.sender); }}
                                            style={{ background: colorForSender(msg.sender?.id) }}
                                            title={msg.sender?.name}
                                        >
                                            {msg.sender?.avatar
                                                ? <img src={msg.sender.avatar} alt={msg.sender?.name || ''} loading="lazy" decoding="async" />
                                                : <span>{(msg.sender?.name || '?').charAt(0).toUpperCase()}</span>}
                                        </div>
                                    )}
                                    <div
                                        className={`message-bubble ${actionBar === msg.id?.toString() ? 'message-bubble-selected' : ''} ${selectedMessageIds.has(msg.id?.toString()) ? 'message-selected' : ''} ${swipingMsgId === msg.id?.toString() ? 'message-bubble-swiping' : ''} ${msg.isPending || msg.status === 'sending' ? 'message-pending' : ''} ${msg.status === 'failed' ? 'message-failed' : ''}`}
                                        style={swipingMsgId === msg.id?.toString() ? { transform: `translateX(${msg.sender.id === currentUser._id ? -swipeOffset : swipeOffset}px)` } : undefined}
                                        onMouseDown={() => { if (!selectionMode) handleLongPressStart(msg.id?.toString()); }}
                                        onMouseUp={handleLongPressEnd}
                                        onClick={(e) => {
                                            // Si estamos en modo selección, click corto = toggle (no abrir actionBar).
                                            if (selectionMode) {
                                                e.stopPropagation();
                                                toggleSelection(msg.id?.toString());
                                            }
                                        }}
                                        onTouchStart={(e) => { handleLongPressStart(msg.id?.toString()); if (!selectionMode) handleSwipeStart(e, msg); }}
                                        onTouchMove={(e) => { if (!selectionMode) handleSwipeMove(e, msg); }}
                                        onTouchEnd={(e) => {
                                            handleLongPressEnd();
                                            if (selectionMode) {
                                                // Solo toggle si NO fue un long-press (long-press ya hizo el toggle).
                                                if (!longPressActive.current) toggleSelection(msg.id?.toString());
                                            } else {
                                                handleSwipeEnd(msg);
                                            }
                                        }}
                                        onContextMenu={(e) => e.preventDefault()}
                                    >
                                        {selectedMessageIds.has(msg.id?.toString()) && (
                                            <div className="message-select-check" aria-hidden="true">✓</div>
                                        )}
                                        {!selectionMode && actionBar === msg.id?.toString() && (
                                            <div className="action-bar" onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                                                {EMOJIS.map(emoji => (
                                                    <button key={emoji} className="reaction-emoji-btn" onClick={(e) => { e.stopPropagation(); handleReaction(msg.id, emoji); setActionBar(null); }}>{emoji}</button>
                                                ))}
                                                <div className="action-bar-separator" />
                                                <button className="action-btn" onClick={(e) => { e.stopPropagation(); setReplyingTo(buildReplyTarget(msg)); setActionBar(null); }}>
                                                    <Icons.Reply size={18} />
                                                </button>
                                                {msg.sender?.id === currentUser._id && (!msg.mediaType || msg.mediaType === 'text') && !msg.isDeleted && (Date.now() - new Date(msg.timestamp).getTime() < 15 * 60 * 1000) && (
                                                    <button className="action-btn" onClick={(e) => { e.stopPropagation(); handleStartEdit(msg); setActionBar(null); }}>
                                                        <Icons.Edit size={18} />
                                                    </button>
                                                )}
                                                <div className="action-bar-separator" />
                                                <button className="action-btn action-btn-delete" onClick={(e) => { e.stopPropagation(); setDeleteModal(msg.id?.toString()); setActionBar(null); }}>
                                                    <Icons.Trash size={18} />
                                                </button>
                                            </div>
                                        )}

                                        {conversation.type === 'group' && msg.sender?.id?.toString() !== currentUser._id?.toString() && !msg.isSystemMessage && (
                                            <div
                                                className="message-sender-name"
                                                onClick={(e) => { e.stopPropagation(); openSenderProfile(msg.sender); }}
                                                style={{ color: colorForSender(msg.sender?.id), cursor: 'pointer' }}
                                            >
                                                {msg.sender?.name}
                                            </div>
                                        )}

                                        {msg.isForwarded && !msg.isDeleted && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#999', fontStyle: 'italic', marginBottom: '2px' }}>
                                                <Icons.Forward size={11} color="#999" /> Reenviado
                                            </div>
                                        )}

                                        {msg.replyToText && (
                                            <div
                                                className="reply-preview-bubble"
                                                onClick={(e) => { e.stopPropagation(); jumpToMessage(msg.replyTo || msg.replyToId); }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {msg.replyToMediaUrl && (msg.replyToMediaType === 'image' || msg.replyToMediaType === 'video') && (
                                                    <img
                                                        src={msg.replyToMediaUrl}
                                                        alt=""
                                                        loading="lazy"
                                                        decoding="async"
                                                        className="reply-preview-thumb"
                                                    />
                                                )}
                                                <div className="reply-preview-body">
                                                    <span className="reply-preview-sender">{msg.replyToSender}</span>
                                                    <span className="reply-preview-text">{msg.replyToText}</span>
                                                </div>
                                            </div>
                                        )}

                                        {msg.mediaType === 'contact' && msg.contactCard && !msg.isDeleted && (() => {
                                            const cc = msg.contactCard;
                                            const initial = (cc.name || '?').charAt(0).toUpperCase();
                                            const isSelf = cc.userId?.toString() === currentUser._id?.toString();
                                            const iAmSender = msg.sender?.id?.toString() === currentUser._id?.toString();
                                            // El botón "Añadir a contactos" solo aparece para el RECEPTOR
                                            // (yo no envié esta tarjeta) y si no es mi propio perfil.
                                            const showAddBtn = !iAmSender && !isSelf;
                                            const openCardProfile = () => {
                                                if (!cc.userId) return;
                                                // Abre la ficha estilo nativo (SharedContactProfile)
                                                // con TODOS los datos enriquecidos del backend +
                                                // cualquier campo extra que tenga allUsers.
                                                const full = allUsers.find(u => u._id?.toString() === cc.userId?.toString()) || {};
                                                setSharedContactInfo({
                                                    userId: cc.userId,
                                                    name: cc.name || full.name || '',
                                                    avatar: cc.avatar || full.avatar || '',
                                                    phone: cc.phone || full.phone || '',
                                                    email: cc.email || full.email || '',
                                                    handle: cc.handle || full.handle || '',
                                                    language: cc.language || full.language || '',
                                                    region: cc.region || full.region || '',
                                                    country: cc.country || full.country || ''
                                                });
                                            };
                                            return (
                                                <div className="contact-card-bubble">
                                                    <div className="contact-card-top" onClick={(e) => { e.stopPropagation(); openCardProfile(); }}>
                                                        <div className="contact-card-avatar" style={{ background: colorForSender(cc.userId) }}>
                                                            {cc.avatar
                                                                ? <img src={cc.avatar} alt="" />
                                                                : <span>{initial}</span>}
                                                        </div>
                                                        <div className="contact-card-info">
                                                            <div className="contact-card-name">{cc.name || t.chat.contactCard.noName}</div>
                                                            <div className="contact-card-sub">{t.chat.contactCard.type}</div>
                                                        </div>
                                                    </div>
                                                    <div className="contact-card-actions">
                                                        <button
                                                            className="contact-card-btn contact-card-btn-secondary"
                                                            onClick={(e) => { e.stopPropagation(); openCardProfile(); }}
                                                        >
                                                            {t.chat.contactCard.viewProfile}
                                                        </button>
                                                        {showAddBtn && (
                                                            <button
                                                                className="contact-card-btn contact-card-btn-primary"
                                                                onClick={(e) => { e.stopPropagation(); handleAddCardToPhoneContacts(cc); }}
                                                            >
                                                                {t.chat.contactCard.add}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {msg.mediaType === 'channel' && msg.channelCard && !msg.isDeleted && (() => {
                                            const ch = msg.channelCard;
                                            const initial = (ch.name || '?').charAt(0).toUpperCase();
                                            const iAmSender = msg.sender?.id?.toString() === currentUser._id?.toString();
                                            const iAmOwner = ch.ownerId?.toString() === currentUser._id?.toString();
                                            const alreadyFollowing = followedChannelsLocal.has(ch.channelId?.toString());
                                            const showFollowBtn = !iAmSender && !iAmOwner;
                                            return (
                                                <div className="channel-card-bubble">
                                                    <div className="channel-card-top">
                                                        <div className="channel-card-avatar" style={{ background: 'var(--orange, #ff8c42)' }}>
                                                            {ch.avatar
                                                                ? <img src={ch.avatar} alt="" />
                                                                : <span>{initial}</span>}
                                                        </div>
                                                        <div className="channel-card-info">
                                                            <div className="channel-card-label">CANAL DE LATIDOS</div>
                                                            <div className="channel-card-name">{ch.name || '(sin nombre)'}</div>
                                                            {ch.handle && <div className="channel-card-sub">@{ch.handle}</div>}
                                                            {ch.ownerName && <div className="channel-card-sub">Por {ch.ownerName}</div>}
                                                            {typeof ch.subscribersCount === 'number' && ch.subscribersCount > 0 && (
                                                                <div className="channel-card-sub">{ch.subscribersCount} seguidor{ch.subscribersCount !== 1 ? 'es' : ''}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {showFollowBtn && (
                                                        <div className="channel-card-actions">
                                                            <button
                                                                className={`channel-card-btn ${alreadyFollowing ? 'channel-card-btn-following' : 'channel-card-btn-follow'}`}
                                                                disabled={alreadyFollowing}
                                                                onClick={(e) => { e.stopPropagation(); handleFollowChannel(ch); }}
                                                            >
                                                                {alreadyFollowing ? '✓ Siguiendo' : 'Seguir'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}

                                        {msg.mediaType === 'image' && msg.mediaUrl && !msg.isDeleted && (
    <div className="message-image-container" onClick={() => setImageViewer(msg.mediaUrl)}>
        <img src={msg.mediaUrl} alt="Imagen enviada" loading="lazy" decoding="async" className="message-image" />
    </div>
)}

                                        {msg.mediaType === 'audio' && msg.mediaUrl && !msg.isDeleted && (() => {
    const mid = msg.id?.toString();
    const same = isSameLanguage(msg);
    const sLang = msg.originalLanguage || msg.language;
    const rLang = msg.isOriginal ? (msg.receiverLanguage || conversation.otherUser?.language) : msg.language;
    return (
    <div className="message-audio-container">
        <audio controls className="message-audio" src={msg.mediaUrl} />
        {msg.transcription && !same && (
            <>
                <div className="message-text" style={{ marginTop: '4px', fontSize: '13px' }}>
                    {msg.isOriginal ? msg.transcription : (msg.translatedTranscription || msg.transcription)}
                </div>
                {msg.isOriginal && msg.translatedTranscription && msg.translatedTranscription !== msg.transcription && (
                    <div className="message-original">{msg.translatedTranscription}</div>
                )}
                {!msg.isOriginal && msg.originalTranscription && msg.originalTranscription !== (msg.translatedTranscription || msg.transcription) && (
                    <div className="message-original">{msg.originalTranscription}</div>
                )}
                <div className="tts-buttons" style={{ marginTop: '4px' }}>
                    <TtsButtons
                        mid={mid}
                        primary={{ text: msg.translatedTranscription, lang: rLang }}
                        secondary={{ text: msg.originalTranscription || msg.transcription, lang: sLang }}
                        voice={msg.sender?.voice}
                        activeId={activeTtsId}
                        pausedId={ttsPausedId}
                        onPlay={handleTtsPlay}
                        onPause={handleTtsPause}
                        onResume={handleTtsResume}
                        onStop={handleTtsStop}
                    />
                </div>
            </>
        )}
    </div>
    );
})()}






                                        {msg.mediaType === 'document' && msg.mediaUrl && !msg.isDeleted && (
                                            <div className="message-document-container">
                                                <div className="message-document-link" onClick={async () => {
                                                    setDocumentModal(msg);
                                                    if (conversations.length === 0 && groups.length === 0) {
                                                        try {
                                                            const [convRes, groupRes, usersRes] = await Promise.all([getConversations(currentUser._id), getUserGroups(currentUser._id), getAllUsers()]);
                                                            setConversations(convRes.data.conversations || []);
                                                            setGroups(groupRes.data.groups || []);
                                                            setAllUsers((usersRes.data.users || []).filter(u => u._id !== currentUser._id && !u.phone?.startsWith('widget_')));
                                                        } catch (err) { showToast(`Error al cargar conversaciones: ${friendlyError(err)}`); }
                                                    }
                                                }} style={{ cursor: 'pointer' }}>
                                                    <span className="message-document-icon">{getFileIcon(msg.fileName || msg.originalText)}</span>
                                                    <span className="message-document-name">{msg.fileName || msg.originalText || 'Documento'}</span>
                                                    <Icons.Document size={18} color="var(--text-secondary)" />
                                                </div>
                                            </div>
                                        )}

                                        {msg.mediaType === 'video' && msg.mediaUrl && !msg.isDeleted && (() => {
    const mid = msg.id?.toString();
    const same = isSameLanguage(msg);
    const sLang = msg.originalLanguage || msg.language;
    const rLang = msg.isOriginal ? (msg.receiverLanguage || conversation.otherUser?.language) : msg.language;
    // Thumbnail Cloudinary: primer frame del video. Cloudinary auto-genera
    // un .jpg cuando cambias la extensión del .mp4 al pedir el recurso.
    const videoPoster = msg.mediaUrl.replace(/\.[a-z0-9]{2,4}$/i, '.jpg');
    return (
    <div className="message-video-container">
        <video id={`video-${mid}`} controls className="message-video"
            preload="metadata" poster={videoPoster}
            onEnded={() => { if (activeTtsId === mid || ttsPausedId === mid) handleTtsStop(mid); }}
        >
            <source src={msg.mediaUrl} type="video/mp4" />
        </video>
        <button className="message-media-download" aria-label="Descargar video"
            onClick={(e) => { e.stopPropagation(); handleMediaDownload(msg.mediaUrl, 'video'); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 4v12" />
                <polyline points="6 11 12 17 18 11" />
                <path d="M5 20h14" />
            </svg>
        </button>
        {msg.transcriptionPending && !msg.transcription && (
            <div className="message-transcribing-indicator">
                <span className="message-transcribing-dot"></span>
                <span className="message-transcribing-dot"></span>
                <span className="message-transcribing-dot"></span>
                <span className="message-transcribing-text">{t.chat.transcribing || 'Transcribiendo subtítulos...'}</span>
            </div>
        )}
        {msg.transcription && !same && (
            <>
                <div className="message-text" style={{ marginTop: '4px', fontSize: '13px' }}>
                    {msg.isOriginal ? (msg.text || msg.transcription) : (msg.translatedTranscription || msg.transcription)}
                </div>
                {msg.isOriginal && msg.translatedTranscription && msg.translatedTranscription !== msg.transcription && (
                    <div className="message-original">{msg.translatedTranscription}</div>
                )}
                {!msg.isOriginal && msg.originalTranscription && msg.originalTranscription !== (msg.translatedTranscription || msg.transcription) && (
                    <div className="message-original">{msg.originalTranscription}</div>
                )}
                <div className="tts-buttons" style={{ marginTop: '4px' }}>
                    <TtsButtons
                        mid={mid}
                        primary={{ text: msg.translatedTranscription, lang: rLang }}
                        secondary={{ text: msg.originalTranscription || msg.transcription, lang: sLang }}
                        voice={msg.sender?.voice}
                        activeId={activeTtsId}
                        pausedId={ttsPausedId}
                        onPlay={(id, text, lang, voice) => handleTtsPlay(id, text, lang, voice, id)}
                        onPause={handleTtsPause}
                        onResume={handleTtsResume}
                        onStop={handleTtsStop}
                    />
                </div>
            </>
        )}
    </div>
    );
})()}

                                        {msg.text && msg.mediaType !== 'audio' && msg.mediaType !== 'document' && msg.mediaType !== 'video' && (() => {
    const mid = msg.id?.toString();
    const same = isSameLanguage(msg);
    const sLang = msg.originalLanguage || msg.language;
    const rLang = msg.isOriginal ? (conversation.otherUser?.language) : msg.language;
    return (
                                            <>
                                                <div className={`message-text ${msg.isDeleted ? 'message-deleted' : ''}`}>{linkify(msg.text)}</div>
                                                {/* Placeholder y traducción solo aplican a chats 1-a-1: en grupos
                                                    el backend traduce por receptor y no devuelve translatedText
                                                    en la HTTP response. Sin esta guarda, el placeholder se
                                                    quedaba pulsando para siempre en los mensajes de grupo. */}
                                                {!same && msg.isOriginal && !msg.isDeleted && conversation.type !== 'group' && (
                                                    msg.translatedText?.trim() && msg.translatedText !== msg.text
                                                        ? <div className="message-original">{linkify(msg.translatedText)}</div>
                                                        : (msg.isPending || msg.status === 'sending')
                                                            ? <div className="message-original message-translating">{t.chat.translating}</div>
                                                            : null
                                                )}
                                                {!same && !msg.isOriginal && msg.originalText?.trim() && msg.originalText !== msg.text && !msg.isDeleted && (
                                                    <div className="message-original">{linkify(msg.originalText)}</div>
                                                )}
                                                {!msg.isDeleted && !same && (() => {
                                                    // Primary: el idioma del texto visible. Secondary: la otra
                                                    // versión (traducida si soy sender, original si soy receiver).
                                                    const secondaryText = msg.isOriginal
                                                        ? (msg.translatedText !== msg.text ? msg.translatedText : null)
                                                        : (msg.originalText !== msg.text ? msg.originalText : null);
                                                    const secondaryLang = msg.isOriginal ? rLang : sLang;
                                                    return (
                                                        <div className="tts-buttons">
                                                            <TtsButtons
                                                                mid={mid}
                                                                primary={{ text: msg.text, lang: msg.language }}
                                                                secondary={secondaryText && secondaryLang
                                                                    ? { text: secondaryText, lang: secondaryLang }
                                                                    : null}
                                                                voice={msg.sender?.voice}
                                                                activeId={activeTtsId}
                                                                pausedId={ttsPausedId}
                                                                onPlay={handleTtsPlay}
                                                                onPause={handleTtsPause}
                                                                onResume={handleTtsResume}
                                                                onStop={handleTtsStop}
                                                            />
                                                        </div>
                                                    );
                                                })()}
                                            </>
    );
})()}

                                        <div className="message-time">
                                            {new Date(msg.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                                            {msg.isEdited && <span style={{ opacity: 0.7, marginLeft: '4px', fontStyle: 'italic' }}> · editado</span>}
                                            {msg.sender?.id?.toString() === currentUser._id?.toString() && (
                                                <span className={`message-status ${msg.status || 'sent'}`}>
                                                    {msg.status === 'sending' && (
                                                        <span title="Enviando…" aria-label="Enviando">
                                                            {' '}<svg width="11" height="11" viewBox="0 0 16 16" style={{ display:'inline-block', verticalAlign:'middle', opacity: 0.7 }}>
                                                                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" fill="none" />
                                                                <line x1="8" y1="8" x2="8" y2="4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                                <line x1="8" y1="8" x2="10.5" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                            </svg>
                                                        </span>
                                                    )}
                                                    {msg.status === 'failed' && (
                                                        <span
                                                            title="Error al enviar — toca para reintentar"
                                                            style={{ color: '#dc2626', fontWeight: 700, marginLeft: 4, cursor: 'pointer' }}
                                                            onClick={(e) => { e.stopPropagation(); handleRetrySend(msg); }}
                                                        > ⚠ ↻</span>
                                                    )}
                                                    {msg.status === 'read' && ' ✓✓'}
                                                    {msg.status === 'delivered' && ' ✓✓'}
                                                    {(!msg.status || msg.status === 'sent') && ' ✓'}
                                                </span>
                                            )}
                                        </div>

                                        {msg.reactions && msg.reactions.length > 0 && (
                                            <div className="reactions-display" style={{ cursor: 'pointer' }} onClick={() => setReactionsModal(msg.reactions)}>
                                                {getReactionSummary(msg.reactions).map(({ emoji, count }) => (
                                                    <span key={emoji} className="reaction-badge">{emoji} {count > 1 && count}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        className="side-forward-btn"
                                        onClick={(e) => { e.stopPropagation(); handleOpenForward(msg); }}
                                        aria-label="Reenviar"
                                        title="Reenviar"
                                    >
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="2.8"
                                             strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="15 17 20 12 15 7" />
                                            <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                                )}
                              </React.Fragment>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </>
                )}
                {(showScrollDown || newMessagesCount > 0) && (
                    <button
                        className="scroll-down-fab"
                        onClick={scrollToBottom}
                        aria-label="Ir al último mensaje"
                    >
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="3"
                             strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                            <polyline points="6 15 12 21 18 15" />
                        </svg>
                        {newMessagesCount > 0 && (
                            <span className="scroll-down-fab-badge">
                                {newMessagesCount > 99 ? '99+' : newMessagesCount}
                            </span>
                        )}
                    </button>
                )}
            </div>

            {imagePreview && (
                <div className="image-preview-container">
                    <div className="image-preview-box">
                        <img src={imagePreview} alt="Preview" className="image-preview" />
                        <button className="cancel-image-btn" onClick={handleCancelImage}><Icons.Close size={14} color="white" /></button>
                    </div>
                    <input type="text" className="caption-input" placeholder={t.chat.addCaption} value={newMessage} onChange={(e) => { setNewMessage(e.target.value); emitTypingEvent(); }} spellCheck={true} lang={langToISO(currentUser.language)} autoCorrect="on" autoCapitalize="sentences" />
                    <button className="send-image-btn" onClick={handleSendImage} disabled={uploading}>{uploading ? t.chat.sending : t.chat.sendImage}</button>
                </div>
            )}

            {documentPreview && (
                <div className="audio-preview-container">
                    <div className="audio-preview-box">
                        <div className="document-preview-info">
                            <span className="document-preview-icon">{getFileIcon(documentPreview)}</span>
                            <span className="document-preview-name">{documentPreview}</span>
                        </div>
                        <button className="cancel-audio-btn" onClick={handleCancelDocument}><Icons.Close size={14} color="white" /></button>
                    </div>
                    <button type="button" className="send-audio-btn" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSendDocument(); }} disabled={uploading}>
                        {uploading ? t.chat.translating : t.chat.sendDocument}
                    </button>
                </div>
            )}

            {isRecording && (
                <div className="audio-preview-container">
                    <div className="audio-preview-box" style={{ justifyContent: 'center', gap: '12px' }}>
                        <span style={{ color: '#e53935', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Icons.MicrophoneActive size={18} /> {recordingSeconds}s
                        </span>
                        <button className="cancel-audio-btn" style={{ background: '#e53935', color: 'white', width: 'auto', borderRadius: 8, padding: '4px 12px' }} onClick={handleStopRecording}>
                            <Icons.Stop size={14} color="white" />
                        </button>
                        <button className="cancel-audio-btn" onClick={handleCancelAudio}><Icons.Close size={14} color="white" /></button>
                    </div>
                </div>
            )}

            {!isRecording && audioPreview && (
                <div className="audio-preview-container">
                    <div className="audio-preview-box">
                        <audio src={audioPreview} controls className="audio-preview" />
                        <button className="cancel-audio-btn" onClick={handleCancelAudio}><Icons.Close size={14} color="white" /></button>
                    </div>
                    <button type="button" className="send-audio-btn" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSendAudio(); }} disabled={uploading}>
                        {uploading ? t.chat.sending : t.chat.sendAudio}
                    </button>
                </div>
            )}

            {videoPreview && (
                <div className="video-preview-container">
                    <div className="video-preview-box">
                        {/* preload=metadata fuerza al WebView a leer el primer
                            frame del archivo local y mostrarlo como thumbnail
                            antes de pulsar play. */}
                        <video src={videoPreview} controls className="video-preview" preload="metadata" poster={videoPreviewPoster || undefined} />
                        <button className="cancel-video-btn" onClick={handleCancelVideo}><Icons.Close size={14} color="white" /></button>
                    </div>
                    <input type="text" className="caption-input" placeholder={t.chat.addCaption} value={newMessage} onChange={(e) => { setNewMessage(e.target.value); emitTypingEvent(); }} spellCheck={true} lang={langToISO(currentUser.language)} autoCorrect="on" autoCapitalize="sentences" />
                    <button className="send-video-btn" onClick={handleSendVideo} disabled={uploading}>{uploading ? t.chat.processing : t.chat.sendVideo}</button>
                </div>
            )}

            {isTyping && typingUser && (
                <div className="typing-indicator">
                    <span className="typing-name">{typingUser}</span>
                    <span className="typing-dots"><span></span><span></span><span></span></span>
                </div>
            )}

            {replyingTo && (
                <div className="reply-preview-bar">
                    <div className="reply-preview-content">
                        <span className="reply-preview-name">{replyingTo.senderName}</span>
                        <span className="reply-preview-msg">{replyingTo.text}</span>
                    </div>
                    <button className="reply-cancel-btn" onClick={() => setReplyingTo(null)}><Icons.Close size={16} /></button>
                </div>
            )}

            {editingMessage && (
                <div className="reply-preview-bar" style={{ background: 'rgba(245,160,0,0.1)', borderLeft: '3px solid #F5A000' }}>
                    <div className="reply-preview-content">
                        <span className="reply-preview-name" style={{ color: '#F5A000' }}>
                            <Icons.Edit size={13} /> Editando mensaje
                        </span>
                        <span className="reply-preview-msg">{editingMessage.text}</span>
                    </div>
                    <button className="reply-cancel-btn" onClick={handleCancelEdit}><Icons.Close size={16} /></button>
                </div>
            )}

            {conversation.isFormerMember && (
                <div className="former-member-notice">{t.chat.formerMember}</div>
            )}

            {!imagePreview && !videoPreview && !audioPreview && !isRecording && !documentPreview && !conversation.isFormerMember && (
                <form className="message-input-container" onSubmit={handleSend}>
                    <input type="file" ref={fileInputRef} accept="image/*" onChange={handleImageSelect} style={{ display: 'none' }} />
                    <input type="file" ref={videoInputRef} accept="video/*" onChange={handleVideoSelect} style={{ display: 'none' }} />
                    <input type="file" ref={galleryInputRef} accept="image/*,video/*" onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        if (file.type.startsWith('image/')) handleImageSelect(e);
                        else if (file.type.startsWith('video/')) handleVideoSelect(e);
                    }} style={{ display: 'none' }} />
                    <input type="file" ref={audioInputRef} accept="audio/*" onChange={handleAudioFileSelect} style={{ display: 'none' }} />
                    <input type="file" ref={documentInputRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt" onChange={handleDocumentSelect} style={{ display: 'none' }} />

                    <div className="attach-menu-wrapper" ref={attachMenuRef}>
                        <button type="button" className="attach-plus-btn" onClick={() => setShowAttachMenu(!showAttachMenu)}>
                            <Icons.Plus color="white" size={20} />
                        </button>
                        {showAttachMenu && (
                            <div className="attach-menu">
                                <button type="button" className="attach-menu-item" onClick={() => setShowAttachSubMenu(!showAttachSubMenu)}>
                                    <Icons.Clip size={18} color="var(--orange)" /> {t.chat.attach.replace('📎 ', '')}
                                </button>
                                {showAttachSubMenu && (
                                    <div className="attach-submenu">
                                        <button type="button" className="attach-menu-item" onClick={() => { documentInputRef.current?.click(); setShowAttachMenu(false); setShowAttachSubMenu(false); }}>
                                            <Icons.Document size={18} color="var(--text-secondary)" /> {t.chat.document.replace('📄 ', '')}
                                        </button>
                                        <button type="button" className="attach-menu-item" onClick={() => { galleryInputRef.current?.click(); setShowAttachMenu(false); setShowAttachSubMenu(false); }}>
                                            <Icons.Image size={18} color="var(--text-secondary)" /> {t.chat.gallery.replace('🖼️ ', '')}
                                        </button>
                                        <div style={{ padding: '2px 12px 6px', fontSize: '10px', color: 'var(--text-secondary)', opacity: 0.7 }}>
                                            Fotos máx 10MB · Videos máx 100MB
                                        </div>
                                    </div>
                                )}
                                <button type="button" className="attach-menu-item" onClick={() => { handleOpenCamera(); setShowAttachMenu(false); }}>
    <Icons.Camera size={18} color="var(--text-secondary)" /> {t.chat.camera.replace('📷 ', '')}
</button>
                                <button type="button" className="attach-menu-item" onClick={() => { setShowContactPicker(true); setShowAttachMenu(false); setShowAttachSubMenu(false); }}>
                                    <Icons.User size={18} color="var(--text-secondary)" /> Contacto
                                </button>
                                <button type="button" className="attach-menu-item" onClick={() => { setShowChannelPicker(true); setShowAttachMenu(false); setShowAttachSubMenu(false); }}>
                                    <Icons.Pulse size={18} color="var(--text-secondary)" /> Latido
                                </button>
                                <div className="attach-menu-separator" />
                                <button type="button" className={`attach-menu-item ${isRecording ? 'attach-menu-item-active' : ''}`} onClick={() => { if (isRecording) { handleStopRecording(); setShowAttachMenu(false); } else { handleStartRecording(); } }}>
                                    <Icons.Microphone size={18} color={isRecording ? 'var(--orange)' : 'var(--text-secondary)'} />
                                    {isRecording ? `${recordingSeconds}s` : t.chat.audio.replace('🎙️ ', '')}
                                </button>
                            </div>
                        )}
                    </div>

                                        <input
                                            type="text"
                                            className="message-input"
                                            placeholder={`${t.chat.typingPlaceholder} ${currentUser.language}...`}
                                            value={newMessage}
                                            onChange={(e) => { setNewMessage(e.target.value); emitTypingEvent(); }}
                                            spellCheck={true}
                                            lang={langToISO(currentUser.language)}
                                            autoCorrect="on"
                                            autoCapitalize="sentences"
                                        />

                    <button
                        type="submit"
                        className="send-button"
                        disabled={!newMessage.trim()}
                        aria-label={t.chat.send}
                    >
                        <Icons.Send color="white" size={18} />
                    </button>
                </form>
            )}

            {forwardModal && (
                <ForwardModal
                    conversations={conversations}
                    groups={groups}
                    allUsers={allUsers}
                    targets={forwardTargets}
                    onToggleTarget={handleToggleForwardTarget}
                    onSend={handleSendForward}
                    onClose={() => setForwardModal(null)}
                />
            )}

            {showContactPicker && (
                <ContactPickerModal
                    allUsers={allUsers}
                    currentUser={currentUser}
                    onSelect={handleSendContact}
                    onClose={() => setShowContactPicker(false)}
                />
            )}

            {showChannelPicker && (
                <ChannelPickerModal
                    currentUser={currentUser}
                    onSelect={handleSendChannel}
                    onClose={() => setShowChannelPicker(false)}
                />
            )}

            {sharedContactInfo && (
                <SharedContactProfile
                    contact={sharedContactInfo}
                    canAddToPhone={sharedContactInfo.userId?.toString() !== currentUser._id?.toString()}
                    onAddToPhone={(cc) => { handleAddCardToPhoneContacts(cc); setSharedContactInfo(null); }}
                    onClose={() => setSharedContactInfo(null)}
                />
            )}


            {reactionsModal && (() => {
                const grouped = {};
                reactionsModal.forEach(r => {
                    if (!grouped[r.emoji]) grouped[r.emoji] = [];
                    grouped[r.emoji].push(r);
                });
                const findUserName = (uid) => {
                    const id = uid?.toString();
                    if (id === currentUser._id?.toString()) return ui.common.you;
                    if (conversation?.otherUser?.id?.toString() === id) return conversation.otherUser.name;
                    const member = conversation?.members?.find(m => m._id?.toString() === id || m.id?.toString() === id);
                    if (member) return member.name;
                    const u = allUsers.find(u => u._id?.toString() === id);
                    return u?.name || ui.common.user;
                };
                return (
                    <div style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }} onClick={() => setReactionsModal(null)}>
                        <div style={{
                            background: 'white', borderRadius: '14px', width: '90%', maxWidth: '380px',
                            maxHeight: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column'
                        }} onClick={e => e.stopPropagation()}>
                            <div style={{ padding: '14px 18px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <strong style={{ fontFamily: 'Nunito, sans-serif', fontSize: '15px' }}>{ui.common.reactions}</strong>
                                <button onClick={() => setReactionsModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>
                                    <Icons.Close size={18} />
                                </button>
                            </div>
                            <div style={{ overflowY: 'auto', flex: 1 }}>
                                {Object.entries(grouped).map(([emoji, reactions]) => (
                                    <div key={emoji} style={{ borderBottom: '1px solid #f3f3f3' }}>
                                        <div style={{ padding: '10px 18px 6px', fontSize: '13px', color: '#888' }}>
                                            <span style={{ fontSize: '20px', marginRight: '6px' }}>{emoji}</span>
                                            {reactions.length}
                                        </div>
                                        {reactions.map((r, i) => (
                                            <div key={i} style={{ padding: '8px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{
                                                    width: '32px', height: '32px', borderRadius: '50%', background: '#F5A000',
                                                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '14px', fontWeight: 700
                                                }}>
                                                    {findUserName(r.userId).charAt(0).toUpperCase()}
                                                </div>
                                                <span style={{ fontSize: '14px', fontFamily: 'Nunito, sans-serif' }}>{findUserName(r.userId)}</span>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {deleteModal && (
                <div className="delete-modal-overlay" onClick={() => setDeleteModal(null)}>
                    <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="delete-modal-title">{t.chat.deleteMsg}</h3>
                        <button className="delete-modal-btn" onClick={() => handleDeleteMessage(false)}>{t.chat.deleteForMe}</button>
                        {messages.find(m => m.id?.toString() === deleteModal)?.sender?.id === currentUser._id && (
                            <button className="delete-modal-btn delete-modal-btn-all" onClick={() => handleDeleteMessage(true)}>{t.chat.deleteForAll}</button>
                        )}
                        <button className="delete-modal-btn delete-modal-btn-cancel" onClick={() => setDeleteModal(null)}>{t.chat.cancel}</button>
                    </div>
                </div>
            )}

            {bulkDeleteModal && (
                <div className="delete-modal-overlay" onClick={() => setBulkDeleteModal(false)}>
                    <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="delete-modal-title">
                            ¿Eliminar {selectedMessageIds.size} {selectedMessageIds.size === 1 ? 'mensaje' : 'mensajes'}?
                        </h3>
                        <button className="delete-modal-btn" onClick={() => handleBulkDelete(false)}>{t.chat.deleteForMe}</button>
                        {canBulkDeleteForAll() && (
                            <button className="delete-modal-btn delete-modal-btn-all" onClick={() => handleBulkDelete(true)}>{t.chat.deleteForAll}</button>
                        )}
                        <button className="delete-modal-btn delete-modal-btn-cancel" onClick={() => setBulkDeleteModal(false)}>{t.chat.cancel}</button>
                    </div>
                </div>
            )}

            {showLeaveGroupModal && (
                <div className="delete-modal-overlay" onClick={() => setShowLeaveGroupModal(false)}>
                    <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="delete-modal-title">{t.chat.leaveGroupConfirm}</h3>
                        <p style={{ padding: '0 20px', color: '#666', fontSize: '14px' }}>{t.chat.leaveGroupWarning}</p>
                        {isAdminUser && conversation.admins?.length <= 1 && (
                            <p style={{ padding: '0 20px', color: '#e53e3e', fontSize: '13px' }}>{t.chat.leaveGroupAdmin}</p>
                        )}
                        <button className="delete-modal-btn delete-modal-btn-all" onClick={() => handleLeaveGroup()} disabled={isAdminUser && conversation.admins?.length <= 1}>{t.chat.leave}</button>
                        <button className="delete-modal-btn delete-modal-btn-cancel" onClick={() => setShowLeaveGroupModal(false)}>{t.chat.cancel}</button>
                    </div>
                </div>
            )}

            {showGroupSettings && conversation.type === 'group' && (
                <GroupSettingsModal
                    group={conversation}
                    currentUser={currentUser}
                    initialTab={groupSettingsTab}
                    onClose={() => setShowGroupSettings(false)}
                    onMembersUpdated={() => { setShowGroupSettings(false); loadMessages(); }}
                    onGroupDeleted={onBack}
                />
            )}
                               
            {documentModal && (
                <DocumentModal
                    msg={documentModal}
                    currentUser={currentUser}
                    conversations={conversations}
                    groups={groups}
                    allUsers={allUsers}
                    onClose={() => setDocumentModal(null)}
                    onForward={async (msg, targets) => {
                        for (const targetId of targets) {
                            const isGroup = groups.some(g => g.id === targetId);
                            const conv = !isGroup ? conversations.find(c => c.id === targetId) : null;
                            const directUser = !isGroup && !conv ? allUsers.find(u => u._id === targetId) : null;
                            const receiverId = conv?.otherUser?.id || directUser?._id;
                            if (isGroup) {
                                await sendEditedDocument({ senderId: currentUser._id, groupId: targetId, text: msg.extractedText, fileName: msg.fileName || msg.originalText });
                            } else if (receiverId) {
                                await sendEditedDocument({ senderId: currentUser._id, receiverId, text: msg.extractedText, fileName: msg.fileName || msg.originalText });
                            }
                        }
                        setDocumentModal(null);
                    }}
                />
            )}

            <ImageViewer src={imageViewer} onClose={() => setImageViewer(null)} />
            {showUserInfo && conversation.type !== 'group' && (
                <UserInfoModal
                    user={conversation.otherUser}
                    currentUserLanguage={currentUser?.language}
                    onClose={() => setShowUserInfo(false)}
                />
            )}
            {/* Click en nombre/avatar de un participante en un grupo abre
                este modal con su perfil. */}
            {groupUserInfo && (
                <UserInfoModal
                    user={groupUserInfo}
                    currentUserLanguage={currentUser?.language}
                    onClose={() => setGroupUserInfo(null)}
                />
            )}
            {showSupportPanel && (
                <div className="sup-overlay" onClick={() => setShowSupportPanel(false)}>
                    <div className="sup-overlay-inner" onClick={e => e.stopPropagation()}>
                        <SupportChat
                            currentUser={currentUser}
                            onBackToChat={() => setShowSupportPanel(false)}
                        />
                    </div>
                </div>
            )}

            {showCamera && (
                <CameraModal
                    onClose={() => setShowCamera(false)}
                    latidosDisabled={!myFirstChannel}
                    onSendToChat={(file, type) => {
                        if (type === 'photo') {
                            setSelectedImage(file);
                            setImagePreview(URL.createObjectURL(file));
                        } else {
                            setSelectedVideo(file);
                            setVideoPreview(URL.createObjectURL(file));
                        }
                    }}
                    onPublishToLatidos={(file, type) => {
                        if (myFirstChannel) {
                            setPendingPublish({ file, type, mode: 'latido' });
                        }
                    }}
                    onCreateStory={(file, type) => {
                        if (myFirstChannel) {
                            setPendingPublish({ file, type, mode: 'story' });
                        }
                    }}
                />
            )}

            {pendingPublish && myFirstChannel && (
                <PublishModal
                    channel={myFirstChannel}
                    mode={pendingPublish.mode}
                    currentUser={currentUser}
                    initialMedia={{ file: pendingPublish.file, type: pendingPublish.type }}
                    onClose={() => setPendingPublish(null)}
                    onPublished={() => setPendingPublish(null)}
                />
            )}

            {devToast && (
                <div onClick={() => setDevToast(null)} style={{
                    position: 'fixed', bottom: '80px', left: '50%', transform: 'translateX(-50%)',
                    background: devToast.type === 'error' ? '#c62828' : '#2e7d32',
                    color: 'white', padding: '10px 18px', borderRadius: '10px',
                    fontSize: '13px', maxWidth: '80vw', zIndex: 9999,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px'
                }}>
                    <span>🛠 DEV</span>
                    <span>{devToast.msg}</span>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Modal "Añadir a contactos del móvil" (formulario completo).
// Pre-rellena nombre y teléfono del remitente desconocido. El usuario
// puede editar nombre, añadir email, notas, y guardar. Usa Capacitor
// Contacts plugin para escribir en la agenda nativa del teléfono.
// ─────────────────────────────────────────────────────────────────────
function AddContactForm({ initial = {}, onCancel, onSaved }) {
    // Sugerencia: el nombre que vino del perfil de KAIXO sirve como
    // PLACEHOLDER, no como prefill rígido. El usuario empieza con el campo
    // vacío y puede aceptar la sugerencia (escribiéndola) o teclear otro.
    const suggestedName = initial.name && initial.name.trim() ? initial.name.trim() : '';
    const [name, setName] = useState('');
    const [phone, setPhone] = useState(initial.phone || '');
    const [email, setEmail] = useState(initial.email || '');
    const [notes, setNotes] = useState(initial.notes || '');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const handleSave = async () => {
        // Si el usuario no escribió nada, aceptamos la sugerencia del perfil
        // (placeholder) como nombre final. Solo bloqueamos si tampoco hay
        // sugerencia disponible.
        const finalName = name.trim() || suggestedName;
        if (!finalName) { setErr('El nombre es obligatorio'); return; }
        if (!phone.trim()) { setErr('El teléfono es obligatorio'); return; }

        let ContactsPlugin = null;
        try { ContactsPlugin = require('@capacitor-community/contacts').Contacts; } catch {}
        if (!ContactsPlugin?.createContact) {
            setErr('Esta acción solo funciona en la app móvil instalada.');
            return;
        }

        setBusy(true);
        setErr('');
        try {
            const contact = {
                name: { given: finalName },
                phones: [{ number: phone.trim(), isPrimary: true, type: 'mobile' }]
            };
            if (email.trim()) {
                contact.emails = [{ address: email.trim(), type: 'home' }];
            }
            if (notes.trim()) {
                contact.note = notes.trim();
            }
            await ContactsPlugin.createContact({ contact });
            onSaved && onSaved();
        } catch (e) {
            setErr('No se pudo guardar: ' + (e?.message || 'error desconocido'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="add-contact-overlay" onClick={onCancel}>
            <div className="add-contact-modal" onClick={e => e.stopPropagation()}>
                <h3>Añadir a contactos</h3>
                <p className="add-contact-sub">Se guardará en la agenda de tu teléfono.</p>
                <div className="add-contact-field">
                    <label>Nombre *</label>
                    <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder={suggestedName || 'Nombre del contacto'}
                        maxLength={80}
                    />
                    {suggestedName && !name && (
                        <small className="add-contact-hint">
                            Sugerencia desde su perfil: <strong>{suggestedName}</strong>. Escribe encima para cambiarlo.
                        </small>
                    )}
                </div>
                <div className="add-contact-field">
                    <label>Teléfono *</label>
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} maxLength={20} />
                </div>
                <div className="add-contact-field">
                    <label>Email</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} maxLength={100} />
                </div>
                <div className="add-contact-field">
                    <label>Notas</label>
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} maxLength={200} rows={2} />
                </div>
                {err && <div className="add-contact-err">{err}</div>}
                <div className="add-contact-foot">
                    <button className="add-contact-cancel" onClick={onCancel} disabled={busy}>Cancelar</button>
                    <button className="add-contact-save" onClick={handleSave} disabled={busy}>
                        {busy ? 'Guardando…' : 'Guardar'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ChatWindow;
